import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Form,
  Spinner,
  Breadcrumb,
  Button,
  Modal,
  Nav,
  Accordion
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useForm, FormProvider, Controller, useWatch } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faCalendarAlt,
  faArrowRight,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { formatDate } from "assets/js/common";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import MultiSelect from "components/common/multiSelect";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import moment from "moment";
import Select, { components } from "react-select";
import {
  fetchRoleAndClaims, 
  handleLoadingAndAccess,  
  generateGUID, 
  fetchTemplatesByCustomer,
  dispatchBatchNotification, 
  fetchLeaveConflictMap,
  fetchRecommendedEmployees,
  partitionRecommendedEmployees,
  sortEmployeesAscending,
  checkEmployeeAvailability,
  clearEmployeeAvailabilityCache,
  PlanningConflictModal
} from "../../assets/js/common";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import DatePicker from "react-datepicker";
import TimeInput from "../proposals/timeInput";
import { setHours, setMinutes } from "date-fns";
import { useAppContext } from "providers/appProvider";
import { getNthWeekdayOfMonth, getWeekdayKey, getOccurrenceLabel, describeRecurrence, getOccurrenceCount, localTimeToUtcHHmm, utcHHmmToLocal } from "../../utils/recurrenceHelpers";

function AddNewOrder() {
  const { t, i18n } = useTranslation();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [proposals, setProposals] = useState([]);
  const [selectedProposalNumber, setSelectedProposalNumber] = useState("");
  const [selectedProposalData, setSelectedProposalData] = useState(null);
  const [loadingProposalDetails, setLoadingProposalDetails] = useState(false);
  const [taskWithSubtasks, setTaskWithSubtasks] = useState([]);
  const [taskAssignments, setTaskAssignments] = useState({});
  const [allEmployees, setAllEmployees] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [selectedCleaners, setSelectedCleaners] = useState([]);
  const [recommendedEmployeeGroups, setRecommendedEmployeeGroups] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [conflictMap, setConflictMap] = useState({});
  const [showPlanningConflictModal, setShowPlanningConflictModal] = useState(false);
  const [pendingConflictData, setPendingConflictData] = useState(null);
  const recommendationRequestRef = useRef(0);
  const recommendationDebounceRef = useRef(null);
  const [isOrderExists, setIsOrderExists] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [relationData, setRelationData] = useState(null);
  const [editStartDate, setEditStartDate] = useState("");
  const [editEndDate, setEditEndDate] = useState("");
  const [editStartTime, setEditStartTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const startDateRef = useRef(null);
  const [proposalId, setProposalId] = useState(null);
  const [dueDate, setDueDate] = useState(null);
  const [contactperson, setContactperson] = useState("");
  const locationData = useLocation();
  const [location, setLocation] = useState("");
  const pathSegments = locationData.pathname.split("/");
  const selectedRelationId = pathSegments.length === 4 ? pathSegments[2] : null;
  const [selectedclient, setSelectedClientName] = useState("");
  const [relation, setRelation] = useState([]);
  const [relationId, setRelationId] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [data, setData] = useState([]);
  const [showProposalTaskModal, setShowProposalTaskModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [taskSelectedDays, setTaskSelectedDays] = useState({});
  const [taskOneTimeDates, setTaskOneTimeDates] = useState({});
  const [loadingProposalTasks, setLoadingProposalTasks] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [activeAccordionKey, setActiveAccordionKey] = useState("1");
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isStartDateOpen, setIsStartDateOpen] = useState(false);
  const [isEndDateOpen, setIsEndDateOpen] = useState(false); 

  const selectedClientInfo = data.find(
    (client) => client.RelationNumber === selectedClient
  );

  const {
    config: { location: userLocation },
  } = useAppContext();

  const normalizeTime = (date) =>
    moment(date)
      .set({ year: 2000, month: 0, date: 1 });

  const schema = yup.object({
    editStartDate: yup
      .date()
      .required(t("startDateRequired"))
      .typeError(t("invalidStartDate"))
      .test(
        "startDate-validation",
        function (value) {
          if (!value) return true;

          const today = moment().startOf("day");
          const start = moment(value).startOf("day");

          const { isOrderExists } = this.options.context || {};

          const isAllTasksOneTime =
            taskWithSubtasks.length > 0 &&
            taskWithSubtasks.every(
              t => t?.recurrence?.frequency === "OneTime"
            );

          //  RULE 1 — Existing order cannot move to past
          if (isOrderExists && start.isBefore(today)) {
            return this.createError({
              message: t("startDateCannotBeInPast")
            });
          }

          //  RULE 2 — One-time order cannot be in past
          if (isAllTasksOneTime && start.isBefore(today)) {
            return this.createError({
              message: (t("oneTimeTaskPast"))
            });
          }

          return true;
        }
      ),

    editEndDate: yup
      .date()
      .nullable()
      .typeError(t("invalidEndDate"))
      .test(
        "endDate-validation",
        function (endDate) {
          const { editStartDate } = this.parent;

          if (!editStartDate || !endDate) return true;

          const start = moment(editStartDate).startOf("day");
          const end = moment(endDate).startOf("day");
          const today = moment().startOf("day");

          if (!start.isValid() || !end.isValid()) return true;

          const isAllTasksOneTime =
            taskWithSubtasks.length > 0 &&
            taskWithSubtasks.every(
              t => t?.recurrence?.frequency === "OneTime"
            );
          // RULE 1 — End date cannot be past
          if (end.isBefore(today)) {
            return this.createError({
              message: t("endDateCannotBePast") || "End date cannot be in the past."
            });
          }

          if (isAllTasksOneTime && start.isBefore(today)) {
            return this.createError({
              message: (t("oneTimeTaskPast"))
            });
          }

          //  RULE 3 — Normal logic
          if (end.isBefore(start)) {
            return this.createError({
              message: t("endDateAfterStartDate")
            });
          }

          const oneTimeTaskDates = taskWithSubtasks
            .filter(t => t?.recurrence?.frequency === "OneTime")
            .map(t => {
              const override = taskOneTimeDates[t.ProposalTaskId];
              return moment(override || t.recurrence?.advancedRecurrence?.date);
            })
            .filter(d => d.isValid());

          if (oneTimeTaskDates.some(d => d.startOf("day").isAfter(end))) {
            return this.createError({
              message: t("endDateBeforeTaskDate") || "End date cannot be before a task's date."
            });
          }

          return true;
        }
      ),

    editStartTime: yup
      .date()
      .required(t("startTimeRequired"))
      .typeError(t("invalidStartTime")),
    editEndTime: yup
      .date()
      .nullable()
      .typeError(t("invalidEndTime"))
      .test(
        "endTime-after-startTime",
        t("endTimeAfterStartTime"),
        function (endTime) {
          const { editStartTime } = this.parent;

          if (endTime && editStartTime) {
            const start = normalizeTime(editStartTime);
            const end = normalizeTime(endTime);
            return end.isSameOrAfter(start);
          }
          return true;
        }
      )
  }
  ).concat(
    yup.object().shape(
      Object.fromEntries(
        taskWithSubtasks.map((task) => [
          [`editStartTime-${task.ProposalTaskId}`, yup
            .string()
            .required(t("startTimeRequired"))],
        ]).flat()
      )
    )
  );

  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    context: { isOrderExists },
    defaultValues: {
      editStartDate: selectedProposalData?.StartDate
        ? new Date(selectedProposalData.StartDate)
        : null,
      editEndDate: selectedProposalData?.EndDate
        ? new Date(selectedProposalData.EndDate)
        : null,
      editStartTime: selectedProposalData?.StartTime
        ? new Date(selectedProposalData.StartTime)
        : null,
      editEndTime: selectedProposalData?.EndTime
        ? new Date(selectedProposalData.EndTime)
        : null,
    }
  });

  const {
    control,
    formState: { errors },
    register,
    reset,
    trigger,
    setValue,
    getValues,
    watch
  } = methods;
  const editStartTimeref = watch("editStartTime");
  const watchedStartDate = watch("editStartDate");
  const watchedEndDate = watch("editEndDate");

  useEffect(() => {
    const loadTemplates = async () => {
      const data = await fetchTemplatesByCustomer(i18n.language);
      setTemplates(data);
    };

    loadTemplates();
  }, [i18n.language]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const fieldsWithErrors = Object.keys(errors);
      trigger(fieldsWithErrors);
    }
  }, [i18n.language]);

  useEffect(() => {
    if (selectedProposalNumber) {
      handleProposalChange(selectedProposalNumber);
    }
  }, [selectedProposalNumber]);

  useEffect(() => {
    fetchRoleAndClaims({
      navigate,
      claimType: "orders",
      claimValue: "create",
      setHasAccess,
      setIsLoading,
    });
  }, [navigate]);

  useEffect(() => {
    if (!editStartTimeref || !taskWithSubtasks.length) return;

    taskWithSubtasks.forEach((task) => {
      const fieldName = `editStartTime-${task.ProposalTaskId}`;
      setValue(fieldName, editStartTimeref, { shouldValidate: true });
    });
  }, [editStartTimeref, taskWithSubtasks, setValue]);


  const clientOptions = clients.map((client) => ({
    value: client.RelationNumber,
    label: client.Name,
  }));

  const fetchProposalsByClient = async (clientNumber) => {
    try {
      const locationId = localStorage.getItem("LocationId");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalRoute/getApprovedProposalsByClient/${locationId}/${clientNumber}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      return response.data || [];

    } catch (error) {
      console.error("Error fetching proposals", error);
      return [];
    }
  };

  useEffect(() => {
    if (selectedClient) {
      setLoadingProposalDetails(true);
      fetchProposalsByClient(selectedClient).then((allProposals) => {
        const approvedProposals = allProposals.filter(
          (p) => p.Status === "1" || p.Status === "approved"
        );
        setProposals(approvedProposals);
        setSelectedProposalNumber("");
        setSelectedProposalData(null);
        setTaskAssignments({});
        setLoadingProposalDetails(false);
      });
    } else {
      setProposals([]);
      setSelectedProposalNumber("");
      setSelectedProposalData(null);
      setTaskAssignments({});
    }
  }, [selectedClient]);

  const handleProposalChange = (proposalNumber) => {
    setSelectedProposalNumber(proposalNumber);
    const proposal = proposals.find((p) => p.ProposalNumber === proposalNumber);
    setSelectedProposalData(proposal || null);

    setTaskAssignments({});
    if (proposal) {
      setEditStartDate(formatDate(proposal.StartDate));
      setEditEndDate(formatDate(proposal.EndDate));
      setEditStartTime(proposal.StartTime);
      setEditEndTime("17:00");

      const customerId = localStorage.getItem("customerId");
      const rangeStart = proposal.StartDate;
      const rangeEnd = proposal.EndDate || moment(proposal.StartDate).add(6, "months").format("YYYY-MM-DD");
      fetchLeaveConflictMap(customerId, rangeStart, rangeEnd).then(setConflictMap);
    }

    if (proposal) {
      fetchAndShowProposalTasks(proposal.ProposalId, proposal.ProposalVersionId); // Fetch tasks + subtasks here
      checkExistingOrder(
        proposal.ProposalId,
        selectedClient,
        proposal.StartDate,
        proposal.EndDate
      );
      fetchRelationByNumber(selectedClient)
    } else {
      setTaskWithSubtasks([]); 
    }
  };

  useEffect(() => {
    if (selectedProposalData) {
      reset({
        editStartDate: selectedProposalData?.StartDate ? new Date(selectedProposalData.StartDate) : null,
        editEndDate: selectedProposalData?.EndDate ? new Date(selectedProposalData.EndDate) : null,
        editStartTime: selectedProposalData?.StartTime ? new Date(selectedProposalData.StartTime) : null,
        editEndTime: selectedProposalData?.EndTime ? new Date(selectedProposalData.EndTime) : null,
      });
    }
  }, [selectedProposalData]);

  useEffect(() => {
    if (!selectedProposalData || taskWithSubtasks.length === 0) return;

    const startTime = selectedProposalData?.StartTime
      ? new Date(selectedProposalData.StartTime)
      : null;

    const utcHHmmToFieldValue = (hhmm) => {
      const [hour, minute] = String(hhmm).split(":").map(Number);
      return moment.utc().hours(hour || 0).minutes(minute || 0).seconds(0).milliseconds(0).toDate();
    };

    let firstDailyTimeValue = null;

    taskWithSubtasks.forEach((task) => {
      const dailyTimes = task.recurrence?.frequency === "Daily"
        ? task.recurrence?.advancedRecurrence?.times
        : null;

      if (Array.isArray(dailyTimes) && dailyTimes.length > 0) {
        const fieldValue = utcHHmmToFieldValue(dailyTimes[0]);
        setValue(`editStartTime-${task.ProposalTaskId}`, fieldValue);
        if (!firstDailyTimeValue) firstDailyTimeValue = fieldValue;
      } else if (startTime) {
        setValue(`editStartTime-${task.ProposalTaskId}`, startTime);
      }
    });

    if (firstDailyTimeValue) {
      setValue("editStartTime", firstDailyTimeValue);
    } else if (startTime) {
      setValue("editStartTime", startTime);
    }
  }, [taskWithSubtasks, selectedProposalData, setValue]);

  useEffect(() => {
    const init = {};
    taskWithSubtasks.forEach(task => {
      init[task.ProposalTaskId] = task.recurrence?.selectedDays || [];
    });
    setTaskSelectedDays(init);
  }, [taskWithSubtasks]);

  useEffect(() => {
    const init = {};
    taskWithSubtasks.forEach(task => {
      const rawDate = task.recurrence?.advancedRecurrence?.date;
      init[task.ProposalTaskId] = rawDate ? new Date(rawDate) : null;
    });
    setTaskOneTimeDates(init);
  }, [taskWithSubtasks]);

  const fetchAndShowProposalTasks = async (proposalId, versionId) => {
    setLoadingProposalTasks(true);
    try {
      const params = versionId ? { proposalVersionId: versionId } : {};
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/proposalRoute/${proposalId}/tasksWithDetails`,
        { params, headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      const tasksWithRecurrence = (res.data || []).map((task) => ({
        ...task,
        recurrence: {
          frequency: task.Frequency,
          repeatEvery: task.RepeatEvery,
          selectedDays: (() => {
            try {
              const raw = task.SelectedDays;
              if (Array.isArray(raw)) return raw;
              if (typeof raw === "string") {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) return parsed;
                if (typeof parsed === "object" && parsed !== null) {
                  return parsed.selectedDays || [];
                }
              }
              return [];
            } catch {
              return [];
            }
          })(),
          monthlyDay: task.MonthlyDay,
          advancedRecurrence: (() => {
            try {
              const raw = task.SelectedDays;
              if (typeof raw === "string") {
                const parsed = JSON.parse(raw);
                if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
                  return parsed;
                }
              }
            } catch {}
            return null;
          })()
        },
      }));

      setTaskWithSubtasks(tasksWithRecurrence);
    } catch (error) {
      console.error("Error fetching proposal tasks with details:", error);
      setTaskWithSubtasks([]);
    } finally {
      setLoadingProposalTasks(false);
    }
  };

  const generateOrderNumber = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = String(now.getFullYear()).slice(-2);
    const hour = String(now.getHours()).padStart(2, "0");
    const second = String(now.getSeconds()).padStart(2, "0");
    const milli = String(now.getMilliseconds()).padStart(3, "0");
    return `O${day}${month}${year}${hour}${second}${milli}`;
  };

  const getOrderWeekRangeText = () => {
    if (!selectedProposalData?.StartDate) return "";

    const start = moment(selectedProposalData.StartDate);
    const end = selectedProposalData.EndDate
      ? moment(selectedProposalData.EndDate)
      : moment(start).add(2, "months");

    const startWeek = start.isoWeek();
    const endWeek = end.isoWeek();

    const startYear = start.isoWeekYear();
    const endYear = end.isoWeekYear();

    // If both in the same week
    if (startWeek === endWeek && startYear === endYear) {
      return `Week ${startWeek}`;
    }

    return startYear === endYear
      ? `${startWeek} – ${endWeek}`
      : `${startWeek} (${startYear}) – ${endWeek} (${endYear})`;
  };

  useEffect(() => {
    const locationId = userLocation?.LocationId;
    if (!selectedProposalData || !locationId) return;

    const fetchAllEmployees = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/userRoute/employeesForOrderAssignment`,
          {
            params: { locationId },
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
          }
        );
        const sortedEmployees = [...(res.data || [])].sort((a, b) =>
          (a.Name || "").localeCompare(b.Name || "")
        );
        setAllEmployees(sortedEmployees);
      } catch (err) {
        console.error("Error fetching employees for order assignment", err);
        setAllEmployees([]);
      }
    };

    fetchAllEmployees();
  }, [selectedProposalData, userLocation?.LocationId]);
  const handleOpenAssignModal = (task) => {
    setActiveTaskId(task.ProposalTaskId);
    setSelectedTask(task);
    const assignedCleaners = taskAssignments[task.ProposalTaskId] || [];
    setSelectedCleaners(assignedCleaners);
    setShowAssignModal(true);
  };

  useEffect(() => {
    const assignedCleaners = activeTaskId ? taskAssignments[activeTaskId] || [] : [];
    if (showAssignModal && assignedCleaners.length > 0) {
      scheduleAssignRecommendations(assignedCleaners);
    } else if (showAssignModal) {
      setRecommendedEmployeeGroups(null);
    }
  }, [showAssignModal, activeTaskId]);

  const getWeekdayOccurrenceOfMonth = (year, month, occurrence, weekday) => {
    const dayIndex = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]
      .indexOf(weekday.toLowerCase());
    if (dayIndex === -1) return null;

    let date = moment().year(year).month(month).date(1).startOf("day");
    const matchedDates = [];

    while (date.month() === month) {
      if (date.day() === dayIndex) {
        matchedDates.push(date.clone());
      }
      date.add(1, "day");
    }

    if (occurrence === "1st") return matchedDates[0];
    if (occurrence === "2nd") return matchedDates[1];
    if (occurrence === "3rd") return matchedDates[2];
    if (occurrence === "4th") return matchedDates[3];
    if (occurrence === "last") return matchedDates[matchedDates.length - 1];

    return null;
  };

  const getTaskOccurrenceDates = (task, orderStartDate, orderEndDate) => {
    if (!orderStartDate) return [];
    const freq = task.recurrence?.frequency || task.Frequency;
    if (freq === "OneTime") {
      const override = taskOneTimeDates[task.ProposalTaskId];
      const oneTimeDate = override || task.recurrence?.advancedRecurrence?.date;
      return [oneTimeDate ? moment(oneTimeDate) : moment(orderStartDate)];
    }

    const rangeEnd = orderEndDate ? moment(orderEndDate) : moment(orderStartDate).add(6, "months");
    const dates = [];

    if (freq === "Daily") {
      const adv = task.recurrence?.advancedRecurrence || {};
      const timesList = Array.isArray(adv.times) && adv.times.length > 0 ? adv.times : ["08:00"];

      let cursor = moment(orderStartDate).startOf("day");
      while (cursor.isSameOrBefore(rangeEnd, "day")) {
        const dateStr = cursor.format("YYYY-MM-DD");
        timesList.forEach((timeStr) => {
          const slot = moment.utc(`${dateStr}T${timeStr}:00`).local();
          if (slot.isSameOrAfter(orderStartDate, "day") && slot.isSameOrBefore(rangeEnd, "day")) {
            dates.push(slot);
          }
        });
        cursor.add(1, "day");
      }
    } else if (freq === "Weekly") {
      const days = taskSelectedDays[task.ProposalTaskId] || task.recurrence?.selectedDays || [];
      if (!days.length) return [];
      let cursor = moment(orderStartDate).startOf("isoWeek");
      while (cursor.isSameOrBefore(rangeEnd, "day")) {
        days.forEach((day) => {
          const dayIndex = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
            .indexOf(day.toLowerCase());
          if (dayIndex === -1) return;
          const d = moment(cursor).add(dayIndex, "days");
          if (d.isSameOrAfter(orderStartDate, "day") && d.isSameOrBefore(rangeEnd, "day")) {
            dates.push(d);
          }
        });
        cursor.add(1, "week");
      }
    } else if (freq === "Monthly") {
      const adv = task.recurrence?.advancedRecurrence || {};
      const repeatEvery = task.recurrence?.repeatEvery || task.RepeatEvery || 1;
      let cursor = moment(orderStartDate).startOf("month");
      let monthCounter = 0;

      while (cursor.isSameOrBefore(rangeEnd, "month")) {
        if (monthCounter % repeatEvery === 0) {
          if (adv.monthlyType === "specific_day" || !adv.monthlyType) {
            const mDayRaw = adv.monthlyDay ?? task.recurrence?.monthlyDay ?? task.MonthlyDay ?? 1;
            const clampedDay = mDayRaw === "last" ? cursor.daysInMonth() : Math.min(Number(mDayRaw) || 1, cursor.daysInMonth());
            const targetDate = moment(cursor).date(clampedDay);
            if (targetDate.isSameOrAfter(orderStartDate, "day") && targetDate.isSameOrBefore(rangeEnd, "day")) {
              dates.push(targetDate);
            }
          } else {
            let occurrences = [];
            if (Array.isArray(adv.monthlyOccurrences)) {
              occurrences = adv.monthlyOccurrences;
            } else if (adv.monthlyWeekOccurrence && adv.monthlyWeekday) {
              const parts = adv.monthlyWeekOccurrence.split("_");
              parts.forEach(p => {
                occurrences.push({ occurrence: p, weekday: adv.monthlyWeekday });
              });
            }
            occurrences.forEach((occObj) => {
              const matchedDate = getWeekdayOccurrenceOfMonth(cursor.year(), cursor.month(), occObj.occurrence, occObj.weekday);
              if (matchedDate && matchedDate.isSameOrAfter(orderStartDate, "day") && matchedDate.isSameOrBefore(rangeEnd, "day")) {
                dates.push(matchedDate);
              }
            });
          }
        }
        cursor.add(1, "month");
        monthCounter++;
      }
    } else if (freq === "Yearly") {
      const adv = task.recurrence?.advancedRecurrence || {};
      let cursor = moment(orderStartDate).startOf("year");
      while (cursor.isSameOrBefore(rangeEnd, "year")) {
        const yearlyDateEntries = Array.isArray(adv.dates) && adv.dates.length > 0
          ? adv.dates
          : [{
              day: adv.yearlyDay ?? task.recurrence?.yearlyDay ?? task.YearlyDay ?? 1,
              month: Array.isArray(adv.yearlyMonth)
                ? adv.yearlyMonth[0]
                : (adv.yearlyMonth ?? task.recurrence?.yearlyMonth ?? task.YearlyMonth ?? 0),
            }];

        yearlyDateEntries.forEach((entry) => {
          const monthCursor = moment(cursor).month(Number(entry.month) || 0);
          const clampedDay = entry.day === "last"
            ? monthCursor.daysInMonth()
            : Math.min(Number(entry.day) || 1, monthCursor.daysInMonth());
          const targetDate = moment(monthCursor).date(clampedDay);
          if (targetDate.isSameOrAfter(orderStartDate, "day") && targetDate.isSameOrBefore(rangeEnd, "day")) {
            dates.push(targetDate);
          }
        });
        cursor.add(1, "year");
      }
    }

    return dates;
  };

  const getTaskOccurrenceDatesForAssign = (task, orderStartDate, orderEndDate) => {
    return getTaskOccurrenceDates(task, orderStartDate, orderEndDate);
  };

  const handleAssignCleaner = async (cleanersList, closeModal = false, loadRecommendations = false) => {
    const requestId = ++recommendationRequestRef.current;
    if (!activeTaskId || !cleanersList) return;

    const startDateRaw = getValues("editStartDate");
    const endDateRaw = getValues("editEndDate") || startDateRaw;
    const previouslyAssigned = taskAssignments[activeTaskId] || [];
    const newlyAdded = cleanersList.find(id => !previouslyAssigned.includes(id));
    const drivingEmployeeId = newlyAdded || cleanersList[0];

    if (!newlyAdded && (!loadRecommendations || cleanersList.length === 0)) {
      setSelectedCleaners(cleanersList);
      setTaskAssignments((prev) => ({
        ...prev,
        [activeTaskId]: cleanersList,
      }));
      setRecommendedEmployeeGroups({ recommended: [], others: sortEmployeesAscending(allEmployees) });
      if (closeModal) setShowAssignModal(false);
      return;
    }

    const employeeName = allEmployees.find(e => e.UserId === drivingEmployeeId)?.Name || drivingEmployeeId;
    const activeTask = taskWithSubtasks.find(t => t.ProposalTaskId === activeTaskId);
    const activeStartRaw = getValues(`editStartTime-${activeTaskId}`);
    let timeWindow = null;
    let occurrenceDates = [];

    if (activeTask && activeStartRaw && startDateRaw) {
      const activeStartUtc = moment.utc(activeStartRaw);
      const startTimeOfDay = moment.utc(`1970-01-01T${activeStartUtc.format("HH:mm:ss")}`).local();
      const endTimeOfDay = startTimeOfDay.clone().add(activeTask.DurationMinutes || 60, "minutes");

      timeWindow = { startTimeOfDay, endTimeOfDay };
      occurrenceDates = getTaskOccurrenceDatesForAssign(
        activeTask,
        startDateRaw,
        endDateRaw || startDateRaw
      );
    }

    const inFormSlotsByDay = {};
    taskWithSubtasks
      .filter((task) => task.ProposalTaskId !== activeTaskId)
      .forEach((task) => {
        const assignedIds = taskAssignments[task.ProposalTaskId] || [];

        const otherStartRaw = getValues(`editStartTime-${task.ProposalTaskId}`);
        if (!otherStartRaw) return;

        const isOtherOneTime = task.recurrence?.frequency === "OneTime";
        const otherStartUtc = moment.utc(otherStartRaw);
        const otherStartTimeOfDay = moment.utc(`1970-01-01T${otherStartUtc.format("HH:mm:ss")}`).local();
        const otherEndTimeOfDay = otherStartTimeOfDay.clone().add(task.DurationMinutes || 60, "minutes");

        assignedIds.forEach((employeeId) => {
          const slot = {
            id: task.ProposalTaskId,
            employeeId,
            start: otherStartTimeOfDay.toDate(),
            end: otherEndTimeOfDay.toDate(),
            label: task.taskName,
            clientName: relationData?.Name || null,
          };

          if (isOtherOneTime) {
            (inFormSlotsByDay["__onetime__"] ||= []).push(slot);
          } else {
            (task.recurrence?.selectedDays || []).forEach((day) => {
              const dayKey = day.toLowerCase();
              (inFormSlotsByDay[dayKey] ||= []).push({ ...slot, dayKey });
            });
          }
        });
      });

    const inFormSlots = Object.entries(inFormSlotsByDay).flatMap(([dayKey, slots]) =>
      slots.map((slot) => ({
        ...slot,
        dayKey,
        date: null,
      }))
    );

    const availabilityCheck = (employeeId) => checkEmployeeAvailability({
      employeeId,
      conflictMap,
      startDate: startDateRaw,
      endDate: endDateRaw,
      occurrenceDates: occurrenceDates.length > 0 ? occurrenceDates : undefined,
      timeWindow: timeWindow || undefined,
      inFormSlots,
      customerId: localStorage.getItem("customerId"),
      excludeOrderDetailId: activeTaskId,
    });
    const conflictResult = newlyAdded ? await availabilityCheck(drivingEmployeeId) : null;


    if (
      conflictResult?.type === "leave" ||
      conflictResult?.type === "taskConflict" ||
      conflictResult?.type === "combined"
    ) {
      setPendingConflictData({
        taskId: activeTaskId,
        cleaners: cleanersList,
        employeeName,
        conflictType: conflictResult.type,
      });
      setShowPlanningConflictModal(true);
      return;
    }

    setSelectedCleaners(cleanersList);
    setTaskAssignments((prev) => ({
      ...prev,
      [activeTaskId]: cleanersList,
    }));
    if (cleanersList.length > 0) {
      const customerId = localStorage.getItem("customerId");
      fetchRecommendedEmployees(customerId, cleanersList).then(async (recommendations) => {
        if (!recommendations) {
          if (requestId === recommendationRequestRef.current) setRecommendedEmployeeGroups({ recommended: [], others: sortEmployeesAscending(allEmployees) });
          return;
        }
        const scopedRecommendations = recommendations.filter((recommendation) =>
          allEmployees.some((employee) => String(employee.UserId) === String(recommendation.UserId))
        );
        const groups = await partitionRecommendedEmployees(allEmployees, scopedRecommendations, (employee) => availabilityCheck(employee.UserId));
        if (requestId === recommendationRequestRef.current) setRecommendedEmployeeGroups({ ...groups, drivingEmployeeId });
      }).catch(() => {
        if (requestId === recommendationRequestRef.current) setRecommendedEmployeeGroups({ recommended: [], others: sortEmployeesAscending(allEmployees) });
      });
    }
    if (closeModal) setShowAssignModal(false);
  };

  const scheduleAssignRecommendations = (cleanersList) => {
    clearTimeout(recommendationDebounceRef.current);
    recommendationDebounceRef.current = setTimeout(() => {
      void handleAssignCleaner(cleanersList, false, true);
    }, 275);
  };

  const handleSaveOrder = async () => {
    const valid = await trigger();
    if (!valid) {
      const step2Valid = await trigger([
        "editStartDate",
        "editStartTime",
        "editEndDate",
        "editEndTime",
      ]);
      if (!step2Valid) {
        setCurrentStep(2);
        setActiveAccordionKey("2");
      } else {
        setCurrentStep(3);
        setActiveAccordionKey("3");
      }
      toast.error(t("pleaseFillRequiredFields"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsSaving(true);

    const userId = localStorage.getItem("UserId");
    const locationId = localStorage.getItem("LocationId");
    const customerId = localStorage.getItem("customerId");
    let emailSentSuccessfully = false;

    if (!locationId || !customerId) {
      console.error("LocationId and CustomerId are missing.");
      setIsSaving(false);
      return;
    }

    for (const task of taskWithSubtasks) {
      const freq = task.recurrence?.frequency;
      if (
        freq === "Weekly" &&
        (!taskSelectedDays[task.ProposalTaskId] ||
          taskSelectedDays[task.ProposalTaskId].length === 0)
      ) {
        toast.error(t("selectAtLeastOneWeekday"), {
          position: "top-right",
          autoClose: 2000,
        });
        setIsSaving(false);
        return;
      }
    }

    const startDateRaw = getValues("editStartDate");
    const endDateRaw = getValues("editEndDate");

    const taskDateValidationToday = moment().startOf("day");
    const orderStartForValidation = moment(startDateRaw).startOf("day");
    for (const task of taskWithSubtasks) {
      if (task.recurrence?.frequency !== "OneTime") continue;

      const effectiveDateRaw = taskOneTimeDates[task.ProposalTaskId] || task.recurrence?.advancedRecurrence?.date;
      const effectiveDate = moment(effectiveDateRaw).startOf("day");
      if (!effectiveDate.isValid()) continue;

      if (effectiveDate.isBefore(taskDateValidationToday)) {
        toast.error(t("taskDateCannotBePast") || "Task date cannot be in the past.", {
          position: "top-right",
          autoClose: 2500,
        });
        setIsSaving(false);
        return;
      }

      if (orderStartForValidation.isValid() && effectiveDate.isBefore(orderStartForValidation)) {
        toast.error(t("taskDateBeforeStartDate") || "Task date cannot be before the start date.", {
          position: "top-right",
          autoClose: 2500,
        });
        setIsSaving(false);
        return;
      }
    }

    const newOrderId = uuidv4();

    const proposalStart = moment(startDateRaw);

    const isAllTasksOneTime =
      taskWithSubtasks.length > 0 &&
      taskWithSubtasks.every(
        t => t?.recurrence?.frequency === "OneTime"
      );

    let proposalEnd;

    // CASE 1 → Single OneTime task
    if (isAllTasksOneTime) {
      proposalEnd = moment(proposalStart);
    }
    // CASE 2 → End date provided
    else if (endDateRaw && moment(endDateRaw).isValid()) {
      proposalEnd = moment(endDateRaw);
    }
    // CASE 3 → No end date given: default range scales with the longest recurrence present
    else {
      const frequenciesPresent = taskWithSubtasks.map(t => t?.recurrence?.frequency);
      const defaultMonths = frequenciesPresent.includes("Yearly")
        ? 72
        : frequenciesPresent.includes("Monthly")
          ? 12
          : 6;
      proposalEnd = moment(proposalStart).add(defaultMonths, "months");
    }

    const oneTimeTaskDatesForEnd = taskWithSubtasks
      .filter(t => t?.recurrence?.frequency === "OneTime")
      .map(t => moment(taskOneTimeDates[t.ProposalTaskId] || t.recurrence?.advancedRecurrence?.date))
      .filter(d => d.isValid());
    const latestOneTimeTaskDate = oneTimeTaskDatesForEnd.length > 0
      ? moment.max(oneTimeTaskDatesForEnd)
      : null;
    if (latestOneTimeTaskDate && proposalEnd.isBefore(latestOneTimeTaskDate)) {
      proposalEnd = latestOneTimeTaskDate;
    }

    const today = moment().startOf("day");
    const orderStart = proposalStart.isBefore(today) ? today : proposalStart;
    const orderEnd = proposalEnd;

    const startTimeRaw = getValues("editStartTime");
    const endTimeRaw = getValues("editEndTime");

    const startTime = startTimeRaw ? moment(startTimeRaw) : null;
    const endTime = endTimeRaw ? moment(endTimeRaw) : null;

    const orderPayload = {
      OrderId: newOrderId,
      CustomerId: customerId,
      OrderNumber: generateOrderNumber(),
      ProposalId: selectedProposalData?.ProposalId,
      ProposalVersionId: selectedProposalData?.ProposalVersionId || null,
      RelationId: selectedClient,
      Location: locationId,
      StartDate: orderStart.format("YYYY-MM-DD"),
      EndDate: orderEnd.format("YYYY-MM-DD"),
      Status: "Draft",
      Notes: "",
      CreatedAt: moment().toISOString(),
      CreatedBy: userId,
      StartTime: startTime ? startTime.toISOString() : null,
      EndTime: endTime ? endTime.toISOString() : null,
    };

    const orderDetailsPayload = [];

    const createOrderDetail = (task, cleanerId, taskDate) => {
      const isDailyTask = (task.recurrence?.frequency || task.Frequency) === "Daily";
      const dailyAdv = task.recurrence?.advancedRecurrence || {};
      const timesList = Array.isArray(dailyAdv.times) && dailyAdv.times.length > 0 ? dailyAdv.times : ["08:00"];
      const isMultiSlotDaily = isDailyTask && timesList.length > 1;

      const orderEndRaw = getValues("editEndTime");
      const dateStr = taskDate.format("YYYY-MM-DD");

      // Multi-slot daily tasks use each slot's own taskDate. Single-slot daily and non-daily use Starts At field.
      let taskStartMoment;
      if (isMultiSlotDaily) {
        taskStartMoment = taskDate.clone();
      } else {
        const taskStartTime = getValues(`editStartTime-${task.ProposalTaskId}`);
        taskStartMoment = taskStartTime
          ? (() => {
            const timeUtc = moment.utc(taskStartTime);
            const combined = moment.utc(`${dateStr}T${timeUtc.format("HH:mm:ss")}`).local();
            return moment(`${dateStr} ${combined.format("HH:mm")}`, "YYYY-MM-DD HH:mm");
          })()
          : taskDate.clone();
      }

      let taskEndMoment;
      let durationMinutes;
      if (isMultiSlotDaily) {
        const sortedTimes = timesList
          .map((hhmm) => utcHHmmToLocal(hhmm, taskDate))
          .sort();
        const currentTimeStr = taskStartMoment.format("HH:mm");
        const currentIdx = sortedTimes.indexOf(currentTimeStr);
        const rawDuration = task.DurationMinutes || 60;

        // Cap duration so back-to-back daily slots don't overlap into the next one.
        let cappedDuration = rawDuration;
        if (currentIdx !== -1 && currentIdx < sortedTimes.length - 1) {
          const [nextHour, nextMinute] = sortedTimes[currentIdx + 1].split(":").map(Number);
          const nextMoment = taskStartMoment.clone().hour(nextHour || 0).minute(nextMinute || 0).second(0);
          const gapMinutes = nextMoment.diff(taskStartMoment, "minutes");
          if (gapMinutes > 0) cappedDuration = Math.min(rawDuration, gapMinutes);
        }

        durationMinutes = cappedDuration;
        taskEndMoment = taskStartMoment.clone().add(durationMinutes, "minutes");
      } else {
        taskEndMoment = (() => {
          if (taskStartMoment && orderEndRaw) {
            const endUtc = moment.utc(orderEndRaw);
            const endCombined = moment.utc(`${dateStr}T${endUtc.format("HH:mm:ss")}`).local();
            const candidate = moment(`${dateStr} ${endCombined.format("HH:mm")}`, "YYYY-MM-DD HH:mm");
            return candidate.isAfter(taskStartMoment)
              ? candidate
              : taskStartMoment.clone().add(task.DurationMinutes || 60, "minutes");
          }
          return taskStartMoment
            ? taskStartMoment.clone().add(task.DurationMinutes || 60, "minutes")
            : null;
        })();

        durationMinutes = (taskStartMoment && taskEndMoment)
          ? taskEndMoment.diff(taskStartMoment, "minutes")
          : task.DurationMinutes || 60;
      }

      return {
        OrderDetailId: uuidv4(),
        OrderId: newOrderId,
        TaskId: task.TaskId,
        ProposalTaskId: task.ProposalTaskId,
        WeekStartDate: taskDate.format("YYYY-MM-DD"),
        DayOfWeek: taskDate.format("dddd").toLowerCase(),
        StartTime: taskStartMoment ? taskStartMoment.toISOString() : null,
        EndTime: taskEndMoment ? taskEndMoment.toISOString() : null,
        // DurationMinutes: task.DurationMinutes || 60,
        DurationMinutes: durationMinutes,
        CleanerId: cleanerId,
        Status: "0",
        Location: null,
        Notes: "",
      };
    };

    taskWithSubtasks.forEach((task) => {
      const assignedCleaners = taskAssignments[task.ProposalTaskId] || [];
      const occurrenceDates = getTaskOccurrenceDates(task, orderStart, orderEnd);

      occurrenceDates.forEach((taskDate) => {
        assignedCleaners.forEach((cleanerId) => {
          orderDetailsPayload.push(
            createOrderDetail(task, cleanerId, taskDate)
          );
        });
      });
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/orderRoute/saveOrder`,
        {
          order: orderPayload,
          orderDetails: orderDetailsPayload,
        },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret,
          },
        }
      );
      
      if (response.status === 201) {
        clearEmployeeAvailabilityCache();
        toast.success(t("orderSaved"), {
          position: "top-right",
          autoClose: 2000,
        });
        setTimeout(() => navigate("/orders"), 800);

        // Notifications/emails/WhatsApp — all cleaners in parallel, background
        (async () => {
          try {
            const tasksByCleaner = orderDetailsPayload.reduce((acc, detail) => {
              if (!acc[detail.CleanerId]) acc[detail.CleanerId] = [];
              acc[detail.CleanerId].push(detail);
              return acc;
            }, {});

            const culture = localStorage.getItem("culture") || "nl";
            const location = [
              relationData?.StreetName,
              relationData?.HouseNumber,
              relationData?.PostCode,
              relationData?.City,
            ].filter(Boolean).join(", ");

            const assignmentEntries = Object.entries(tasksByCleaner)
              .map(([cleanerId, details]) => {
                const cleaner = allEmployees.find(c => c.UserId === cleanerId);
                if (!cleaner) return null;

                const firstTask = taskWithSubtasks.find(
                  t => t.ProposalTaskId === details[0].ProposalTaskId
                );

                const uniqueTaskMap = new Map();
                details.forEach(detail => {
                  if (!uniqueTaskMap.has(detail.TaskId)) {
                    const task = taskWithSubtasks.find(
                      t => t.ProposalTaskId === detail.ProposalTaskId
                    );
                    const freq = task?.recurrence?.frequency?.toLowerCase();
                    const translatedFrequency = freq === "onetime" ? t("onetime") : t(freq);

                    uniqueTaskMap.set(detail.TaskId, {
                      taskName: task?.taskName,
                      startTime: detail.StartTime ? moment(detail.StartTime).format("HH:mm") : null,
                      endTime: detail.EndTime ? moment(detail.EndTime).format("HH:mm") : null,
                      frequency: task?.recurrence?.frequency === "OneTime"
                        ? t("onetime")
                        : translatedFrequency,
                    });
                  }
                });

                const orderInfo = {
                  clientName: relationData?.Name || selectedProposalData?.Contactperson,
                  location,
                  startDate: orderPayload.StartDate,
                  endDate: orderPayload.EndDate,
                  tasks: Array.from(uniqueTaskMap.values()),
                };

                return {
                  cleaner: { userId: cleanerId, name: cleaner.Name, email: cleaner.Emailaddress, phone: cleaner.PhoneNumber },
                  data: {
                    orderInfo,
                    culture,
                    organizationName: process.env.REACT_APP_ORGANIZATION_NAME,
                    userId,
                    raisedFrom: userId,
                    isActionRequired: true,
                    sourceId: newOrderId,
                    sourceType: "order",
                    sourceDate: orderPayload.StartDate,
                    inAppMessageCleaner: t("in-AppNotifications.taskAssignedMessage", {
                      taskName: firstTask?.taskName,
                      clientName: relationData?.Name,
                    }),
                    inAppManagerSuffix: t("in-AppNotifications.taskAssignedManagerSuffix", { 
                      taskName: firstTask?.taskName, 
                      clientName: relationData?.Name 
                    }),
                    whatsappTemplateName: `task_assignment_marketing_${culture}`,
                    whatsappParameters: [
                      cleaner.Name,
                      firstTask?.taskName,
                      location,
                      moment(orderPayload.StartDate).format("DD-MM-YYYY"),
                      moment(orderPayload.EndDate).format("DD-MM-YYYY"),
                    ],
                  }
                };
              })
              .filter(Boolean);

            await dispatchBatchNotification({ customerId, eventKey: "TASK_ASSIGNED", entries: assignmentEntries });
          } catch (err) {
            console.error("Background notification error:", err);
          }
        })();
      }
    } catch (error) {
      console.error("Error saving order:", error);
      toast.error(t("orderSaveFailed"), { autoClose: 3000 });
    } finally {
      setIsSaving(false);
    }
  };

  const fetchRelationByNumber = async (relationId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/relationRoute/relationByNumber/${relationId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      setRelationData(response.data);
    } catch (error) {
      console.error("Error fetching relation data:", error);
    }
  };

  const checkExistingOrder = async (
    proposalId,
    relationId,
    proposalStartDate,
    proposalEndDate
  ) => {
    const proposalStart = moment(proposalStartDate);
    const proposalEnd = proposalEndDate ? moment(proposalEndDate) : null;

    const weekStart = moment(proposalStart).startOf("week").add(1, "day"); 
    const weekEnd = moment(weekStart).add(6, "days"); 

    const startDate = proposalStart.format("YYYY-MM-DD");

    let endDate = weekEnd;
    if (proposalEnd && proposalEnd.isSameOrBefore(weekEnd, "day")) {
      endDate = proposalEnd;
    }

    try {

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/orderRoute/checkUpcomingOrder`,
        {
          params: {
            proposalId,
            relationId,
          },
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );

      setIsOrderExists(res.data.exists);
    } catch (err) {
      console.error("Failed to check existing order", err);
    }
  };

  // show toast only once
  useEffect(() => {
    if (isOrderExists) {
      toast.warn(t("orderExistsWarning"), {
        position: "top-right",
        autoClose: 3000,
        icon: <i className="fas fa-exclamation-triangle text-warning" />,
        className:
          "bg-warning-subtle text-warning border-start border-warning border-4",
        progressClassName: "bg-warning",
      });
    }
  }, [isOrderExists, t]);

  useEffect(() => {
    if (isOrderExists) {
      const today = moment().startOf("day").toDate();

      setValue("editStartDate", today, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [isOrderExists, setValue]);

  // reset on proposal change
  useEffect(() => {
    setIsOrderExists(false);
  }, [selectedProposalNumber]);

  const generateProposalNumber = async () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0"); 
    const month = String(now.getMonth() + 1).padStart(2, "0"); 
    const year = String(now.getFullYear()).slice(-2); 
    const hour = String(now.getHours()).padStart(2, "0"); 
    const second = String(now.getSeconds()).padStart(2, "0"); 
    const milli = String(now.getMilliseconds()).padStart(3, "0"); 

    return `OR${day}${month}${year}${hour}${second}${milli}`; 
  };

  useEffect(() => {
    const locationId = userLocation?.LocationId;
    if (!locationId) return;

    const fetchRelations = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate(`${paths.login}`);
        }

        const loggedInUserCustomerId = localStorage.getItem("customerId");
        setCustomerId(loggedInUserCustomerId);

        if (!loggedInUserCustomerId) {
          console.error("CustomerId is missing.");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/relationRoute/relationsForOrders`,
          {
            params: { locationId, customerId: loggedInUserCustomerId },
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
          }
        );
        const sortedData = [...response.data].sort((a, b) => a.Name.localeCompare(b.Name));
        setData(sortedData);
        setClients(sortedData);

        if (selectedRelationId) {
          const relation = response.data.find((r) => r.RelationId === selectedRelationId);
          if (relation) {
            setRelationId(relation.RelationId);
            setCustomerId(relation.CustomerId);
            setRelation(relation);
            setSelectedClientName(relation.Name);
            setValue("client", relation.RelationNumber);
            setLocation(relation.City);
            setContactperson(relation.Contactperson);
          }
        }
        setSelectedClient("");
        setProposals([]);
        setSelectedProposalNumber("");
        setSelectedProposalData(null);
        setTaskWithSubtasks([]);
        setTaskAssignments({});
      } catch (error) {
        console.error("Error fetching relation data:", error);
      }
    };

    fetchRelations();
  }, [userLocation?.LocationId, selectedRelationId, setValue, trigger]);

  useEffect(() => {
    if (!userLocation?.LocationId) return;
    const newProposalId = generateGUID();
    setProposalId(newProposalId);
  }, [userLocation?.LocationId]);


  const CustomGroupHeading = (props) => {
    const groupId = props.data?.id;
    const shouldShowDivider =
      groupId === "clients" || groupId === "proposals"; 

    return (
      <div className="m-0 p-0">
        {shouldShowDivider && (
          <hr className="my-1 border-300" />
        )}
        <components.GroupHeading
          {...props}
          className="px-2 py-1 m-0 fs-10"
        />
      </div>
    );
  };

  // ── ORDER WIZARD STEPS  ──
  const orderSteps = [
    { id: 1, title: t("clientAndProposal"), subtitle: t("clientProposalSub") },
    { id: 2, title: t("orderContractInfo"), subtitle: t("orderContractSub") },
    { id: 3, title: t("taskAssignment"), subtitle: t("taskAssignmentSub") },
  ];
  const handleOrderNext = async () => {
    if (currentStep === 1) {
      if (!selectedClient || !selectedProposalNumber || selectedProposalNumber === "New") {
        toast.error(t("pleaseFillRequiredFields"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
    } else if (currentStep === 2) {
      const valid = await trigger(["editStartDate", "editStartTime", "editEndDate", "editEndTime"]);
      if (!valid) {
        toast.error(t("pleaseFillRequiredFields"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
    }
    setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
    const nextStep = Math.min(currentStep + 1, 3);
    setCurrentStep(nextStep);
    setActiveAccordionKey(String(nextStep));
  };
  const handleOrderBack = () => {
    const prevStep = Math.max(currentStep - 1, 1);
    setCurrentStep(prevStep);
    setActiveAccordionKey(String(prevStep));
  };
  const goToOrderStep = (stepId) => {
    if (stepId === currentStep) return;
    setCurrentStep(stepId);
    setActiveAccordionKey(String(stepId));
  };
  const handleOrderSave = async () => {
    if (!selectedClient || !selectedProposalNumber || selectedProposalNumber === "New") {
      setCurrentStep(1);
      setActiveAccordionKey("1");
      toast.error(t("pleaseFillRequiredFields"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const step2Valid = await trigger([
      "editStartDate",
      "editStartTime",
      "editEndDate",
      "editEndTime",
    ]);
    if (!step2Valid) {
      setCurrentStep(2);
      setActiveAccordionKey("2");
      toast.error(t("pleaseFillRequiredFields"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const anyAssignments = Object.values(taskAssignments).some(
      (assigned) => Array.isArray(assigned) && assigned.length > 0
    );
    if (!anyAssignments) {
      setCurrentStep(3);
      setActiveAccordionKey("3");
      toast.error(t("pleaseAssignTasks"), {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    const allValid = await trigger();
    if (!allValid) {
      setCurrentStep(3);
      setActiveAccordionKey("3");
      toast.error(t("pleaseFillRequiredFields"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    handleSaveOrder();
  };

  const loadingOrRedirect = handleLoadingAndAccess(
    isLoading,
    hasAccess
  );

  if (loadingOrRedirect) {
    return loadingOrRedirect; 
  }

  return (
    <>
      <Row className="g-3 mb-3">
        <Col lg={12} xxl={12}>
          <Card className="mb-3 h-100">
             <Background
                image={corner4}
                className="bg-card rounded-end"
              />
            <Card.Body className="position-relative pe-4 pb-2">
              <Row className="flex-between-center">
                <Col md>
                  <h5 className="mb-2 mb-md-0">{t("newOrder")}</h5>
                  <Breadcrumb>
                    <Breadcrumb.Item
                      as={Link}
                      to="/orders"
                      className="fw-semi-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {t("orders")}
                    </Breadcrumb.Item>
                    <Breadcrumb.Item
                      className="fw-bold mb-1 fs-9 data-font-colour"
                      active
                    >
                      {t("new")}
                    </Breadcrumb.Item>
                  </Breadcrumb>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="g-3 align-items-stretch">

        {/* ── LEFT SIDEBAR ── */}
        <Col xs="12" lg="3" xl="2" className="rf-left-panel step-sidebar-col">
          <Card className="mb-3 h-100 step-row-container order-step-container">
            <Card.Body>
              {orderSteps.map((step, index) => (
                <div
                  key={step.id}
                  role="button"
                  tabIndex={0}
                  className={`d-flex gap-2 pointer p-2 rounded step-row ${
                    currentStep === step.id ? "step-row-active" : ""
                  }`}
                  onClick={() => goToOrderStep(step.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      goToOrderStep(step.id);
                    }
                  }}
                >
                  <div className="d-flex flex-column align-items-center step-circle-col">
                    <span
                      className={`step-circle ${currentStep === step.id ? "active" : ""} ${
                        completedSteps.includes(step.id) ? "completed" : ""
                      }`}
                    >
                      {step.id}
                    </span>
                    {index < orderSteps.length - 1 && (
                      <div
                        className={`step-connector-line ${
                          completedSteps.includes(step.id) ? "completed" : ""
                        } ${currentStep === step.id ? "active" : ""}`}
                      />
                    )}
                  </div>
                  <div className="step-text-col">
                    <h5 className={`fw-medium ${currentStep === step.id ? "text-primary" : ""}`}>
                      {step.title}
                    </h5>
                    <small className="mt-2 text-secondary">{step.subtitle}</small>
                  </div>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>

        {/* ── RIGHT PANEL (Accordion Wizard) ── */}
        <Col xs="12" lg="9" xl="10" className="rf-right-panel rf-form-col">
          <Accordion
            activeKey={activeAccordionKey}
            onSelect={(key) => { if (key) goToOrderStep(Number(key)); }}
            className="rf-accordion"
          >

            {/* ── STEP 1: Client & Proposal ── */}
            <Accordion.Item eventKey="1" className="rf-accordion-item">
              <Accordion.Header>
                <span className="h5 fw-medium mb-0 d-flex align-items-center">
                  <span className="rf-header-icon bg-client">
                    <FontAwesomeIcon icon={faUser} />
                  </span>
                  <span className="rf-header-text">
                    <span>{t("clientAndProposal")}</span>
                    <span className="rf-header-subtitle">{t("clientProposalSub")}</span>
                  </span>
                </span>
              </Accordion.Header>
                            <Accordion.Body id="order-proposal-select-card">
                <Row className="g-4 mb-3">
                  {/* Client Select */}
                  <Col xs={12} md={5}>
                    <Form.Group controlId="clientSelect">
                      <Form.Label className="data-font-size data-font-colour mb-2">
                        {t("client")}
                      </Form.Label>
                      <Select
                        id="clientSelect"
                        options={[
                          {
                            label: t("actions"),
                            options: [{ value: "new", label: t("new") }],
                            id: "actions",
                          },
                          {
                            label: t("clients"),
                            options: clientOptions,
                            id: "clients",
                          },
                        ]}
                        value={clientOptions.find((opt) => opt.value === selectedClient) || null}
                        onChange={(selectedOption) => {
                          if (selectedOption?.value === "new") {
                            navigate("/relations/new-relation");
                          } else {
                            setSelectedClient(selectedOption?.value || "");
                          }
                        }}
                        placeholder={t("searchLabel")}
                        noOptionsMessage={() => t("noOptionsAvailable")}
                        isClearable
                        className="custom-select-container"
                        classNamePrefix="custom-select"
                        components={{ GroupHeading: CustomGroupHeading }}
                      />
                    </Form.Group>
                  </Col>

                  {/* Proposal Select */}
                  <Col xs={12} md={6}>
                    <Form.Group controlId="proposalSelect">
                      <Form.Label className="data-font-size data-font-colour mb-2">
                        {t("proposal")}
                      </Form.Label>
                      <Select
                        id="proposalSelect"
                        isLoading={loadingProposalDetails}
                        options={
                          proposals.length === 0
                            ? [
                              { value: "New", label: t("new") },
                            ]
                            : [
                              {
                                label: t("actions"),
                                options: [{ value: "New", label: t("new") }],
                                id: "actions",
                              },
                              {
                                label: t("proposals"),
                                options: proposals.map((proposal) => {
                                  const templateName =
                                    proposal.TemplateName || t("unknownTemplate");

                                  return {
                                    value: proposal.ProposalNumber,
                                    label: `${templateName} - ${proposal.ProposalNumber}`,
                                  };
                                }),
                                id: "proposals",
                              },
                            ]
                        }
                        value={
                          selectedProposalNumber
                            ? selectedProposalNumber === "New"
                              ? { value: "New", label: t("new") }
                              : (() => {
                                const selectedProposal = proposals.find(
                                  (p) => p.ProposalNumber === selectedProposalNumber
                                );
                                const templateName =
                                  selectedProposal?.TemplateName || t("unknownTemplate");

                                return {
                                  value: selectedProposalNumber,
                                  label: `${templateName} - ${selectedProposalNumber}`,
                                };
                              })()
                            : null
                        }
                        onChange={(selectedOption) => {
                          setSelectedProposalNumber(selectedOption?.value || "");
                          if (selectedOption?.value === "New") {
                            setShowProposalTaskModal(true);
                          }
                        }}
                        isDisabled={!selectedClient}
                        placeholder={t("searchLabel")}
                        noOptionsMessage={() => t("noOptionsAvailable")}
                        isClearable
                        className="custom-select-container"
                        classNamePrefix="custom-select"
                        components={{ GroupHeading: CustomGroupHeading }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>

                        {/* ── STEP 2: Order & Contract Information ── */}
            <Accordion.Item eventKey="2" className="rf-accordion-item">
              <Accordion.Header>
                <span className="h5 fw-medium mb-0 d-flex align-items-center">
                  <span className="rf-header-icon bg-address">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </span>
                  <span className="rf-header-text">
                    <span>{t("orderContractInfo")}</span>
                    <span className="rf-header-subtitle">{t("orderContractSub")}</span>
                  </span>
                </span>
              </Accordion.Header>
              <Accordion.Body>
                {loadingProposalTasks ? (
                  <div className="text-center my-5">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">{t("loading")}</span>
                    </Spinner>
                  </div>
                ) : selectedProposalData && taskWithSubtasks.length > 0 ? (
                  <div className="pt-2">
                    <Row className="g-4">
                      {/* ── Column 1: Client Details ── */}
                      <Col xs={12} md={5}>
                        <h6 className="fw-semi-bold ls mb-3 fs-9 data-font-colour data-font-size">
                          {t("clientDetails")}
                        </h6>

                        {/* Contact Person */}
                        <div className="mb-3">
                          <Form.Label className="data-font-size data-font-colour mb-2">
                            {t("contactPerson")}
                          </Form.Label>
                          <div className="text-700 data-font-size">
                            {selectedProposalData.Contactperson || "-"}
                          </div>
                        </div>

                        {/* Branch */}
                        <div>
                          <Form.Label className="data-font-size data-font-colour mb-2">
                            {t("branch")}
                          </Form.Label>
                          <div className="text-700 data-font-size">
                            {[relationData?.StreetName, relationData?.PostCode, relationData?.City].filter(Boolean).join(", ") || "-"}
                          </div>
                        </div>
                      </Col>

                      {/* ── Column 2: Contract Duration ── */}
                      <Col xs={12} md={7} className="border-start-md ps-md-4">
                        <h6 className="fw-semi-bold ls mb-3 fs-9 data-font-colour data-font-size">
                          {t("contractDuration")}
                        </h6>

                        {/* Row 1: Start Date & Start Time */}
                        <Row className="g-3 mb-3">
                          {/* Start Date */}
                          <Col xs={12} sm={5}>
                            <Form.Group controlId="editStartDate" className="order-date-input">
                              <Form.Label className="data-font-size data-font-colour mb-2">
                                {t("startDate")} <span className="text-danger">*</span>
                              </Form.Label>
                              <div className={`date-picker-wrapper position-relative ${errors.editStartDate ? "is-invalid-wrapper" : ""}`}>
                                <Controller
                                  control={control}
                                  name="editStartDate"
                                  render={({ field }) => (
                                    <DatePicker
                                      id="editStartDate"
                                      ref={startDateRef}
                                      selected={field.value}
                                      onChange={(date) => {
                                        field.onChange(date);
                                        setIsStartDateOpen(false);
                                        trigger("editEndDate");
                                      }}
                                      open={isStartDateOpen}
                                      onInputClick={() => setIsStartDateOpen(true)}
                                      onClickOutside={() => setIsStartDateOpen(false)}
                                       autoComplete="off"
                                      dateFormat="dd-MM-yyyy"
                                      placeholderText="dd-mm-yyyy"
                                      className={`form-control form-control-sm pe-4 ${errors.editStartDate ? "is-invalid" : ""}`}
                                      portalId="date-picker-portal"
                                    />
                                  )}
                                />
                                <span className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 text-muted pe-none" onClick={() => setIsEndDateOpen((prev) => !prev)}>
                                  <FontAwesomeIcon icon={faCalendarAlt} />
                                </span>
                              </div>
                              {errors.editStartDate && (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                  {errors.editStartDate?.message}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>

                          {/* Start Time */}
                          <Col xs={12} sm={5}>
                            <Form.Group controlId="editStartTime" className="order-time-input">
                              <Form.Label className="data-font-size data-font-colour mb-2">
                                {t("startingTime")} <span className="text-danger">*</span>
                              </Form.Label>
                              <Controller
                                control={control}
                                name="editStartTime"
                                render={({ field }) => (
                                  <DatePicker
                                    id="editStartTime"
                                    selected={
                                      field.value
                                        ? (() => {
                                            const timeUtc = moment.utc(field.value);
                                            const dateStr = watchedStartDate
                                              ? moment(watchedStartDate).format("YYYY-MM-DD")
                                              : moment().format("YYYY-MM-DD");
                                            const combined = moment.utc(`${dateStr}T${timeUtc.format("HH:mm:ss")}`).local();
                                            return combined.toDate();
                                          })()
                                        : undefined
                                    }
                                    openToDate={setHours(setMinutes(new Date(), 0), 9)}
                                    onChange={(time) => {
                                      field.onChange(time);
                                      trigger("editEndTime");
                                    }}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={15}
                                    timeCaption="Time"
                                    dateFormat="HH:mm"
                                    timeFormat="HH:mm"
                                    placeholderText="HH:mm"
                                    autoComplete="off"
                                    className={`form-control form-control-sm ${errors.editStartTime ? "is-invalid" : ""}`}
                                    customInput={
                                      <TimeInput className="form-control form-control-sm" autoComplete="off" />
                                    }
                                  />
                                )}
                              />
                              {errors.editStartTime && (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                  {errors.editStartTime?.message}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>

                        {/* Row 2: End Date & End Time */}
                        <Row className="g-3">
                          {/* End Date */}
                          <Col xs={12} sm={5}>
                            <Form.Group controlId="editEndDate" className="order-date-input">
                              <Form.Label className="data-font-size data-font-colour mb-2">
                                {t("endDate")}
                              </Form.Label>
                              <div className={`date-picker-wrapper position-relative ${errors.editEndDate ? "is-invalid-wrapper" : ""}`}>
                                <Controller
                                  control={control}
                                  name="editEndDate"
                                  render={({ field }) => (
                                    <DatePicker
                                      id="editEndDate"
                                      selected={field.value}
                                      onChange={(date) => {
                                        field.onChange(date);
                                        setIsEndDateOpen(false);
                                      }}
                                      open={isEndDateOpen}
                                      onInputClick={() => setIsEndDateOpen(true)}
                                      onClickOutside={() => setIsEndDateOpen(false)}
                                      autoComplete="off"
                                      dateFormat="dd-MM-yyyy"
                                      placeholderText="dd-mm-yyyy"
                                      className={`form-control form-control-sm pe-4 ${errors.editEndDate ? "is-invalid" : ""}`}
                                      portalId="date-picker-portal"
                                    />
                                  )}
                                />
                                <span className="date-picker-calendar-icon position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 text-muted pe-none" onClick={() => setIsEndDateOpen((prev) => !prev)}>
                                  <FontAwesomeIcon icon={faCalendarAlt} />
                                </span>
                              </div>
                              {errors.editEndDate && (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                  {errors.editEndDate?.message}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>

                          {/* End Time */}
                          <Col xs={12} sm={5}>
                            <Form.Group controlId="editEndTime" className="order-time-input">
                              <Form.Label className="data-font-size data-font-colour mb-2">
                                {t("endTime")}
                              </Form.Label>
                              <Controller
                                control={control}
                                name="editEndTime"
                                render={({ field }) => (
                                  <DatePicker
                                    id="editEndTime"
                                    selected={
                                      field.value
                                        ? (() => {
                                            const timeUtc = moment.utc(field.value);
                                            const dateStr = watchedStartDate
                                              ? moment(watchedStartDate).format("YYYY-MM-DD")
                                              : moment().format("YYYY-MM-DD");
                                            const combined = moment.utc(`${dateStr}T${timeUtc.format("HH:mm:ss")}`).local();
                                            return combined.toDate();
                                          })()
                                        : undefined
                                    }
                                    openToDate={setHours(setMinutes(new Date(), 0), 9)}
                                    onChange={field.onChange}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={15}
                                    timeCaption="Time"
                                    dateFormat="HH:mm"
                                    timeFormat="HH:mm"
                                    placeholderText="HH:mm"
                                    autoComplete="off"
                                    className={`form-control form-control-sm ${errors.editEndTime ? "is-invalid" : ""}`}
                                    customInput={
                                      <TimeInput className="form-control form-control-sm" autoComplete="off" />
                                    }
                                  />
                                )}
                              />
                              {errors.editEndTime && (
                                <Form.Control.Feedback type="invalid" className="d-block">
                                  {errors.editEndTime?.message}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    {t("selectProposalToViewDetails")}
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>

            {/* ── STEP 3: Task Assignment ── */}
            <Accordion.Item eventKey="3" className="rf-accordion-item">
              <Accordion.Header>
                <span className="h5 fw-medium mb-0 d-flex align-items-center">
                  <span className="rf-header-icon bg-invoice">
                    <FontAwesomeIcon icon={faUserPlus} />
                  </span>
                  <span className="rf-header-text">
                    <span>{t("taskAssignment")}</span>
                    <span className="rf-header-subtitle">{t("taskAssignmentSub")}</span>
                  </span>
                </span>
              </Accordion.Header>
              <Accordion.Body>
                {loadingProposalTasks ? (
                  <div className="text-center my-5">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">{t("loading")}</span>
                    </Spinner>
                  </div>
                ) : selectedProposalData ? (
                    <>
                    <div className="d-flex justify-content-start align-items-center mb-3">
                      <span className="text-muted fs-9">
                        {t("planningWeek")}: {getOrderWeekRangeText()}
                      </span>
                    </div>
                      {taskWithSubtasks.length === 0 ? (
                        <div className="text-center text-muted py-3">
                          {t("noTasksAvailable")}
                        </div>
                      ) : (
                        taskWithSubtasks.map((task, i) => (
                          <div key={task.ProposalTaskId || i} className="border rounded p-3 mb-3 bg-white shadow-sm">
                            <Row>
                              <Col md={9}>
                                <Row className="align-items-center mb-2">
                                  <Col md={8}>
                                    <p className="mb-0 fw-semibold fs-9 text-dark">
                                      {i + 1}. {task.taskName} (
                                      {task.recurrence.frequency === "OneTime"
                                        ? t(`frequencyLabel.${task.recurrence.frequency.toLowerCase()}`)
                                        : `${getOccurrenceCount(task.recurrence)}x ${t(
                                          `frequencyLabel.${task.recurrence.frequency.toLowerCase()}`
                                        )}`}
                                      )
                                    </p>
                                  </Col>
                                  <Col md={4}>
                                    {task.recurrence?.frequency === "OneTime" ? (
                                      <div className="task-row-date-input-wrap order-task-date-picker position-relative">
                                        <DatePicker
                                          selected={taskOneTimeDates[task.ProposalTaskId] || null}
                                          onChange={(date) => {
                                            if (!date) {
                                              setTaskOneTimeDates((prev) => ({ ...prev, [task.ProposalTaskId]: null }));
                                              return;
                                            }
                                            const selected = moment(date).startOf("day");
                                            const today = moment().startOf("day");
                                            if (selected.isBefore(today)) {
                                              toast.error(t("taskDateCannotBePast") || "Task date cannot be in the past.", {
                                                position: "top-right",
                                                autoClose: 2500,
                                              });
                                              return;
                                            }
                                            if (watchedStartDate && selected.isBefore(moment(watchedStartDate).startOf("day"))) {
                                              toast.error(t("taskDateBeforeStartDate") || "Task date cannot be before the start date.", {
                                                position: "top-right",
                                                autoClose: 2500,
                                              });
                                              return;
                                            }
                                            if (watchedEndDate && selected.isAfter(moment(watchedEndDate).startOf("day"))) {
                                              toast.error(t("taskDateAfterEndDate") || "Task date cannot be after the end date.", {
                                                position: "top-right",
                                                autoClose: 2500,
                                              });
                                              return;
                                            }
                                            setTaskOneTimeDates((prev) => ({ ...prev, [task.ProposalTaskId]: date }));
                                          }}
                                          dateFormat="dd-MM-yyyy"
                                          placeholderText="dd-mm-yyyy"
                                          className="form-control form-control-sm pe-4"
                                        />
                                        <span className="position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 text-muted pe-none">
                                          <FontAwesomeIcon icon={faCalendarAlt} />
                                        </span> 
                                      </div>
                                    ) : task.recurrence?.selectedDays?.length > 0 ? (
                                      [...task.recurrence.selectedDays]
                                        .sort(
                                          (a, b) =>
                                            [
                                              "monday", "tuesday", "wednesday",
                                              "thursday", "friday", "saturday", "sunday"
                                            ].indexOf(a.toLowerCase()) -
                                            [
                                              "monday", "tuesday", "wednesday",
                                              "thursday", "friday", "saturday", "sunday"
                                            ].indexOf(b.toLowerCase())
                                        )
                                        .map((day) => {
                                          const isSelected =
                                            taskSelectedDays[task.ProposalTaskId]?.includes(day);

                                          return (
                                            <span
                                              key={day}
                                              role="button"
                                              onClick={() => {
                                                setTaskSelectedDays(prev => {
                                                  const current = prev[task.ProposalTaskId] || [];
                                                  const updated = current.includes(day)
                                                    ? current.filter(d => d !== day)
                                                    : [...current, day];

                                                  return {
                                                    ...prev,
                                                    [task.ProposalTaskId]: updated
                                                  };
                                                });
                                              }}
                                              className={`badge fw-medium me-1 mb-1 border cursor-pointer ${isSelected
                                                ? "bg-success-subtle text-success"
                                                : "bg-light text-muted"
                                                }`}
                                            >
                                              {t(`days.${day.toLowerCase()}`)}
                                            </span>
                                          );
                                        })
                                    ) : (
                                      <span className="text-muted">--</span>
                                    )}
                                  </Col>
                                </Row>

                                <Row>
                                  <Col>
                                    <div className="d-flex flex-wrap gap-2">
                                      {taskAssignments[task.ProposalTaskId]?.map((id, idx) => {
                                        const cleaner = allEmployees.find((c) => c.UserId === id);
                                        return (
                                          <div
                                            key={idx}
                                            className="d-flex align-items-center rounded-pill px-3 py-1 bg-secondary-subtle text-default fs-10 position-relative"
                                          >
                                            <FontAwesomeIcon icon="user" className="me-2 text-default" />
                                            <span>{cleaner?.Name}</span>
                                            <div
                                              className="position-absolute remove-cleaner-icon"
                                              onClick={() => {
                                                const updatedCleaners = taskAssignments[task.ProposalTaskId].filter(
                                                  (cid) => cid !== id
                                                );
                                                setTaskAssignments((prev) => ({
                                                  ...prev,
                                                  [task.ProposalTaskId]: updatedCleaners,
                                                }));
                                              }}
                                            >
                                              <FontAwesomeIcon icon="times" size="xs" className="text-danger" />
                                            </div>
                                          </div>
                                        );
                                      })}
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => handleOpenAssignModal(task)}
                                        title={t("assign")}
                                      >
                                        <FontAwesomeIcon icon="user-plus" />
                                      </Button>
                                    </div>
                                  </Col>
                                </Row>
                              </Col>

                              <Col md={3} className="d-flex justify-content-start justify-content-md-end mb-2 mt-2 mt-md-0" id="task-start-time-col">
                                <div className="d-flex align-items-start gap-2">
                                  <p className="mb-0 data-font-colour field-label">
                                    {t("startsAt")}
                                  </p>

                                  <div className="task-time-input-wrapper">
                                    <Controller
                                      control={control}
                                      name={`editStartTime-${task.ProposalTaskId}`}
                                      rules={{ required: t("startTimeRequired") }}
                                      render={({ field }) => (
                                        <DatePicker
                                          selected={
                                            field.value
                                              ? (() => {
                                                const timeUtc = moment.utc(field.value);
                                                if (watchedStartDate) {
                                                  const dateStr = moment(watchedStartDate).format("YYYY-MM-DD");
                                                  const combined = moment.utc(`${dateStr}T${timeUtc.format("HH:mm:ss")}`).local();
                                                  return combined.toDate();
                                                }
                                                return field.value;
                                              })()
                                              : undefined
                                          }
                                          openToDate={setHours(setMinutes(new Date(), 0), 9)}
                                          showTimeSelect
                                          showTimeSelectOnly
                                          timeIntervals={15}
                                          timeCaption="Time"
                                          dateFormat="HH:mm"
                                          timeFormat="HH:mm"
                                          placeholderText="HH:mm"
                                          className={`form-control form-control-sm narrow-width-sm ${errors[`editStartTime-${task.ProposalTaskId}`] ? "is-invalid" : ""}`}
                                          onChange={(val) => field.onChange(val)}
                                          customInput={
                                            <TimeInput className="form-control form-control-sm narrow-width-sm" />
                                          }
                                        />
                                      )}
                                    />
                                    <Form.Control.Feedback type="invalid" className="d-block">
                                      {errors[`editStartTime-${task.ProposalTaskId}`]?.message}
                                    </Form.Control.Feedback>
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        ))
                      )}
                  </>
                ) : (
                  <div className="text-center text-muted py-4">
                    {t("selectProposalToViewDetails")}
                  </div>
                )}
              </Accordion.Body>
            </Accordion.Item>

          </Accordion>

          {/* ── FOOTER: Cancel / Back / Next / Save ── */}
          <Card className="mt-2">
            <Card.Body>
              <Row className="justify-content-between align-items-center">
                <Col xs="auto">
                  <Button
                    variant="falcon-default"
                    size="sm"
                    type="button"
                    onClick={() => navigate("/orders")}
                  >
                    {t("cancel")}
                  </Button>
                </Col>
                <Col xs="auto">
                  {currentStep > 1 && (
                    <Button
                      variant="falcon-default"
                      size="sm"
                      className="me-3"
                      type="button"
                      onClick={handleOrderBack}
                    >
                      {t("back")}
                    </Button>
                  )}
                  {currentStep < 3 ? (
                    <Button
                      variant="primary"
                      size="sm"
                      type="button"
                      onClick={handleOrderNext}
                    >
                      {t("next")} <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                    </Button>
                  ) : (
                    <Button
                      data-tour="save-btn"
                      variant="primary"
                      size="sm"
                      type="button"
                      onClick={handleOrderSave}
                      disabled={isSaving}
                    >
                      {isSaving ? t("saving") : t("save")}
                    </Button>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Modal
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        centered
        backdrop="static"
        keyboard={false}
        className={showPlanningConflictModal ? "d-none" : ""}
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-muted fs-9">
            {t("assignCleaner")} {t("to")}{" "}
            {selectedTask?.taskName && <span>– {selectedTask.taskName}</span>}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t("selectCleaner")}</Form.Label>
            <MultiSelect
              noOptionsMessage={() => t("noOptionsAvailable")}
              isMulti
              closeMenuOnSelect={false}
              options={recommendedEmployeeGroups?.recommended?.length > 0 ? [
                { label: t("recommendedEmployees"), options: recommendedEmployeeGroups.recommended.map((cleaner) => ({ value: cleaner.UserId, label: cleaner.Name })) },
                { label: t("allEmployees"), options: recommendedEmployeeGroups.others.map((cleaner) => ({ value: cleaner.UserId, label: cleaner.Name })) },
              ] : sortEmployeesAscending(allEmployees).map((cleaner) => ({ value: cleaner.UserId, label: cleaner.Name }))}
              value={allEmployees
                .filter((emp) => selectedCleaners.includes(emp.UserId))
                .map((emp) => ({
                  value: emp.UserId,
                  label: emp.Name,
                }))}
              onChange={(selected) => {
                const newValues = selected.map((item) => item.value);
                scheduleAssignRecommendations(newValues);
              }}
            />
          </Form.Group>

          {selectedCleaners?.length > 0 && (
            <div className="mt-3 d-flex flex-wrap gap-2">
              {selectedCleaners.map((id) => {
                const cleaner = allEmployees.find((c) => c.UserId === id);
                if (!cleaner) return null;

                return (
                  <div
                    key={id}
                    className="d-flex align-items-center px-3 py-1 rounded-pill bg-light border text-dark small shadow-sm"
                  >
                    <FontAwesomeIcon
                      icon="user"
                      className="me-2 text-primary"
                    />
                    <span className="fw-semibold">{cleaner.Name}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            size="sm"
            variant="falcon-default"
            onClick={() => setShowAssignModal(false)}
          >
            {t("cancel")}
          </Button>

          <Button
            size="sm"
            variant="primary"
            onClick={() => handleAssignCleaner(selectedCleaners, true)}
            disabled={!selectedCleaners || selectedCleaners.length === 0}
          >
            {t("assign")}
          </Button>
        </Modal.Footer>
      </Modal>

      <PlanningConflictModal
        show={showPlanningConflictModal}
        employeeName={pendingConflictData?.employeeName}
        conflictType={pendingConflictData?.conflictType}
        t={t}
        onCancel={() => {
          setShowPlanningConflictModal(false);
          setPendingConflictData(null);
        }}
        onConfirm={() => {
          if (pendingConflictData) {
            setSelectedCleaners(pendingConflictData.cleaners);
            setTaskAssignments((prev) => ({
              ...prev,
              [pendingConflictData.taskId]: pendingConflictData.cleaners,
            }));
          }
          setShowPlanningConflictModal(false);
          setPendingConflictData(null);
        }}
      />

      {/* {showProposalTaskModal && ( */}
      <ProposalTaskModal
        show={showProposalTaskModal}
        handleClose={() => setShowProposalTaskModal(false)}
        proposalId={proposalId}
        templates={templates}
        selectedClient={selectedClient}
        customerId={customerId}
        selectedClientInfo={selectedClientInfo}
        onSaveFinal={async (newProposal) => {
          const updatedProposals = await fetchProposalsByClient(selectedClient);
          const approvedProposals = updatedProposals.filter(
            (p) => p.Status === "1" || p.Status === "approved"
          );
          setProposals(approvedProposals);
          setValue("proposalId", newProposal.ProposalNumber, { shouldValidate: true });
          setSelectedProposalNumber(newProposal.ProposalNumber);
          handleProposalChange(newProposal.ProposalNumber);
        }}
      />
      {/* )} */}
    </>
  );
}

export default AddNewOrder;

function ProposalTaskModal({
  show,
  handleClose,
  onSaveFinal,
  proposalId,
  templates,
  selectedClient,
  customerId,
  selectedClientInfo,
}) {
  const { t, i18n } = useTranslation();
  const { config: { location: userLocation } } = useAppContext();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    watch,
    register,
  } = useForm({
    defaultValues: {
      paymentTerms: "Bank Transfer",
      invoiceType: "Invoice",
      startDate: null,
    },
  });

  const [step, setStep] = useState(1);
  const [selectedWorkType, setSelectedWorkType] = useState(null);
  const [proposalData, setProposalData] = useState({});
  const [taskOptions, setTaskOptions] = useState([]);
  const [selectedFrequency, setSelectedFrequency] = useState("Weekly");
  const [repeatEvery, setRepeatEvery] = useState(1);
  const [selectedDays, setSelectedDays] = useState([]);
  const [monthlyDay, setMonthlyDay] = useState(new Date().getDate());
  const [yearlyMonth, setYearlyMonth] = useState([new Date().getMonth()]);
  const [yearlyDay, setYearlyDay] = useState(new Date().getDate());
  const [isLoading, setIsLoading] = useState(false);
  const startDate = watch("startDate");
  const taskId = watch("taskId");
  const frequency = watch("frequency");
  const [tasks, setTasks] = useState([]);
  const [taskPendingDeleteIdx, setTaskPendingDeleteIdx] = useState(null);

  const [monthlyType, setMonthlyType] = useState("specific_day");
  const [monthlyOccurrences, setMonthlyOccurrences] = useState([{ occurrence: "1st", weekday: "monday" }]);
  const [oneTimeDate, setOneTimeDate] = useState(null);
  const [dailyStartTimes, setDailyStartTimes] = useState([setHours(setMinutes(new Date(), 0), 8)]);
  const [yearlyDates, setYearlyDates] = useState([{ day: new Date().getDate(), month: new Date().getMonth() }]);
  const MAX_MONTHLY_OCCURRENCE_ROWS = 4;
  const MAX_YEARLY_OCCURRENCE_ROWS = 6;

  const dayKeys = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

  const addMonthlyOccurrenceRow = () => {
    setMonthlyOccurrences((prev) =>
      prev.length >= MAX_MONTHLY_OCCURRENCE_ROWS
        ? prev
        : [...prev, { occurrence: "1st", weekday: "monday" }]
    );
  };

  const removeMonthlyOccurrenceRow = (idx) => {
    setMonthlyOccurrences((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev
    );
  };

  const addYearlyDateRow = () => {
    setYearlyDates((prev) =>
      prev.length >= MAX_YEARLY_OCCURRENCE_ROWS
        ? prev
        : [...prev, { day: 1, month: 0 }]
    );
  };

  const removeYearlyDateRow = (idx) => {
    setYearlyDates((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev
    );
  };

  const addDailyStartTimeRow = () => {
    const lastTime = dailyStartTimes[dailyStartTimes.length - 1];
    const lastMoment = moment(lastTime);
    const fourHoursLater = lastMoment.clone().add(4, "hours");
    // 23:45 is the latest slot the 15-minute time picker can select.
    const latestSlotToday = lastMoment.clone().hour(23).minute(45).second(0).millisecond(0);

    // Fall back to the day's last slot if the default would cross midnight.
    const candidate = fourHoursLater.isSame(lastMoment, "day") ? fourHoursLater : latestSlotToday;

    if (!candidate.isAfter(lastMoment)) {
      toast.error(t("dailyTimeOutOfRange") || "No more time slots left in the day.", {
        position: "top-right",
        autoClose: 2500,
      });
      return;
    }

    const candidateStr = candidate.format("HH:mm");
    const isDuplicate = dailyStartTimes.some((t) => moment(t).format("HH:mm") === candidateStr);
    if (isDuplicate) {
      toast.error(t("dailyTimeDuplicate") || "That time is already added.", {
        position: "top-right",
        autoClose: 2500,
      });
      return;
    }

    setDailyStartTimes((prev) => [...prev, candidate.toDate()]);
  };

  const removeDailyStartTimeRow = (idx) => {
    setDailyStartTimes((prev) =>
      prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev
    );
  };

  // useEffect(() => {
  //   if (show) {
  //     fetchTaskData();
  //   }
  // }, [show]);

  useEffect(() => {
    fetchTaskData();
  }, []);

  const fetchTaskData = async () => {
    const customerId = localStorage.getItem("customerId");
    if (!customerId) return;
    const res = await axios.get(`${process.env.REACT_APP_API_URL}/taskRoute/tasks`, {
      params: { customerId },
      headers: {
        Authorization: process.env.REACT_APP_BasicAuthSecret
      }
    });
    setTaskOptions((res.data || []).map(task => ({
      value: task.TaskId,
      label: task.TaskName,
    })));
  };

     const handleDayToggle = (dayKey) => {
    setSelectedDays((prev) =>
      prev.includes(dayKey) ? prev.filter((d) => d !== dayKey) : [...prev, dayKey]
    );
  };

  useEffect(() => {
    const refDate = startDate ? new Date(startDate) : new Date();
    const refDay = getWeekdayKey(refDate.getDay());
    switch (selectedFrequency) {
      case "OneTime":
        setSelectedDays([]);
        if (setValue) {
          dayKeys.forEach((day) => setValue(`weekdays.${day}`, false));
        }
        setOneTimeDate(refDate);
        break;
      case "Daily":
        setSelectedDays([...dayKeys]);
        if (setValue) {
          dayKeys.forEach((day) => setValue(`weekdays.${day}`, true));
        }
        break;
      case "Weekly": {
        const initDays = dayKeys.includes(refDay) ? [refDay] : [];
        setSelectedDays(initDays);
        if (setValue) {
          dayKeys.forEach((day) =>
            setValue(`weekdays.${day}`, initDays.includes(day))
          );
        }
        break;
      }
      case "Monthly": {
        setMonthlyDay(refDate.getDate());
        const { nth, weekday } = getNthWeekdayOfMonth(refDate);
        setMonthlyOccurrences([
          { occurrence: getOccurrenceLabel(nth), weekday: getWeekdayKey(weekday) },
        ]);
        break;
      }
      case "Yearly": {
        setYearlyDay(refDate.getDate());
        setYearlyMonth([refDate.getMonth()]);
        setYearlyDates([{ day: refDate.getDate(), month: refDate.getMonth() }]);
        break;
      }
      default:
        setSelectedDays([]);
        if (setValue) {
          dayKeys.forEach((day) => setValue(`weekdays.${day}`, false));
        }
    }
  }, [selectedFrequency, startDate, setValue]);

  const getWeekdayLabel = (day) => {
    if (!day) return "";
    return t(`fullDaysName.${day}`);
  };

  const getMonthLabel = (mIdx) => {
    const monthName = moment.months()[mIdx];
    return monthName ? t(`months.${monthName.toLowerCase()}`) : "";
  };

  const getOrdinalSuffix = (day, lang) => {
    if (lang === "nl") return "e";
    if (lang === "hi") return "";
    if (day === 1 || day === 21 || day === 31) return "st";
    if (day === 2 || day === 22) return "nd";
    if (day === 3 || day === 23) return "rd";
    return "th";
  };

  const getRecurrenceSummary = () => {
    if (!startDate || !selectedFrequency) return null;

    const dayNames = selectedDays
      .map((day) => getWeekdayLabel(day))
      .join(" , ");

        switch (selectedFrequency) {
      case "OneTime": {
        const formattedDate = moment(oneTimeDate || startDate).format("Do MMMM YYYY");
        return t("recurrenceSummaryOnce", { date: formattedDate });
      }
      case "Daily": {
        const sortedTimes = [...dailyStartTimes]
          .map((t) => moment(t).format("HH:mm"))
          .sort();
        if (sortedTimes.length <= 1) {
          return t("recurrenceSummaryDailySingle", { time: sortedTimes[0] });
        }
        return t("recurrenceSummaryDailyMulti", { count: sortedTimes.length, times: sortedTimes.join(", ") });
      }
      case "Weekly": {
        if (!dayNames) return "";
        return t("recurrenceSummaryWeekly", { days: dayNames });
      }
      case "Monthly": {
        if (monthlyType === 'specific_day') {
          const dayText = monthlyDay === "last" ? t("recurrenceSummaryMonthlyLastDay") : t("recurrenceSummaryMonthlyDayN", { day: monthlyDay });
          return t("recurrenceSummaryMonthlyDayText", { dayText });
        } else {
          const desc = monthlyOccurrences
            .map(o => `${t(o.occurrence)} ${getWeekdayLabel(o.weekday)}`)
            .join(` ${t("listConjunction") || "and"} `);
          return t("recurrenceSummaryMonthlyOccurrence", { desc });
        }
      }
      case "Yearly": {
        const desc = yearlyDates
          .map((entry) => {
            const monthsStr = getMonthLabel(entry.month);
            if (entry.day === "last") {
              return t("recurrenceSummaryYearlyLastDay", { month: monthsStr });
            }
            const suffix = getOrdinalSuffix(entry.day, i18n.language);
            return t("recurrenceSummaryYearlyDateAlt", { day: entry.day, suffix, month: monthsStr });
          })
          .join(` ${t("listConjunction") || "and"} `);
        return t("recurrenceSummaryYearly", { desc });
      }
      default:
        return "";
    }
  };

  const handleAddTask = () => {
    const values = getValues();

    if (!isTaskValid) {
      toast.error(t("fillAllTaskFields"), { position: "top-right", autoClose: 2000 });
      return;
    }

    const taskName =
      taskOptions.find((opt) => opt.value === values.taskId)?.label || "";

    const advancedRecurrence = {};
    if (selectedFrequency === "Daily") {
      advancedRecurrence.times = [...dailyStartTimes]
        .sort((a, b) => moment(a).valueOf() - moment(b).valueOf())
        .map((t) => localTimeToUtcHHmm(t));
    } else if (selectedFrequency === "Monthly") {
      advancedRecurrence.monthlyType = monthlyType;
      if (monthlyType === "specific_day") {
        advancedRecurrence.monthlyDay = monthlyDay;
      } else {
        advancedRecurrence.monthlyOccurrences = monthlyOccurrences;
      }
    } else if (selectedFrequency === "Yearly") {
      advancedRecurrence.dates = yearlyDates;
    } else if (selectedFrequency === "OneTime") {
      advancedRecurrence.date = oneTimeDate
        ? moment(oneTimeDate).format("YYYY-MM-DD")
        : null;
    }

    const newTask = {
      TaskId: values.taskId,
      TaskName: taskName,
      Frequency: selectedFrequency,
      SelectedDays: selectedFrequency === "Weekly"
        ? selectedDays
        : advancedRecurrence,
      MonthlyDay: selectedFrequency === "Monthly" && monthlyType === "specific_day" ? monthlyDay : null,
      Summary: getRecurrenceSummary(),
    };

    setTasks((prev) => [...prev, newTask]); //  add to table

    // Reset task form fields
    setValue("taskId", null);
    setValue("frequency", null);
    setSelectedFrequency("Weekly");
    setSelectedDays([]);
  };

  const handleProposalNext = (data) => {
    setProposalData(data);
    setStep(2);
  };

    const generateProposalNumber = async () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0"); 
    const month = String(now.getMonth() + 1).padStart(2, "0"); 
    const year = String(now.getFullYear()).slice(-2); 
    const hour = String(now.getHours()).padStart(2, "0"); 
    const second = String(now.getSeconds()).padStart(2, "0"); 
    const milli = String(now.getMilliseconds()).padStart(3, "0"); 

    return `OR${day}${month}${year}${hour}${second}${milli}`; 
  };

  const saveProposal = async (proposalData) => {
    const userIdFromSession = localStorage.getItem("UserId");
    if (!userIdFromSession) throw new Error("UserId is missing in localStorage");

    const userResponse = await axios.get(
      `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${userIdFromSession}`, {
      headers: {
        Authorization: process.env.REACT_APP_BasicAuthSecret
      }
    }
    );
    const employeeNumber = userResponse?.data?.UserNumber || null;
    const contactPerson = "Mock Contact";

    const proposalNumber = await generateProposalNumber();
    const finalProposalId = proposalId || generateGUID();

    const {
      editStartDate,
      editEndDate,
      editStartTime,
      editEndTime,
      ...cleanedData
    } = proposalData;
    const formattedStartDate = proposalData.startDate
      ? moment(proposalData.startDate).format("YYYY-MM-DD")
      : null;

    const formattedEndDate = proposalData.endDate
      ? moment(proposalData.endDate).format("YYYY-MM-DD")
      : null;

    const formattedDueDate = formattedStartDate
      ? moment(formattedStartDate).add(14, "days").format("YYYY-MM-DD")
      : null;

    let formattedStartTime = null;
    if (proposalData.startTime) {
      const utcEquivalentMoment = moment(proposalData.startTime).utc();
      formattedStartTime = `${moment().format("YYYY-MM-DD")}T${utcEquivalentMoment.format(
        "HH:mm:ss.SSS"
      )}Z`;
    }

    const selectedTemplate = templates.find(
      (t) => t.TemplateId === proposalData.templateId
    );

    const templateName = selectedTemplate?.TemplateName || null;

    const updatedFormData = {
      ...cleanedData,
      employee: employeeNumber,
      client: selectedClient,
      proposalNumber,
      proposalId: finalProposalId,
      customerId,
      templateName,
      contactperson: selectedClientInfo?.Contactperson || contactPerson,
      location: selectedClientInfo?.City,
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      date: formattedStartDate,
      dueDate: formattedDueDate,
      startTime: formattedStartTime ? moment(formattedStartTime).format("HH:mm:ss") : null,
      locationId: userLocation?.LocationId || null,
      status: "1",
      clientComment: null,
      proposalComment: null,
      isImported: false
    };

    delete updatedFormData.weekdays;

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/proposalRoute/addProposal`,
      updatedFormData, {
      headers: {
        Authorization: process.env.REACT_APP_BasicAuthSecret
      }
    }
    );

    return response.data;
  };

  const saveTask = async (task, proposalId, proposalVersionId) => {
    const fullTask = {
      TaskId: task.TaskId,
      Frequency: task.Frequency,
      RepeatEvery: task.Frequency === "OneTime" ? 0 : task.RepeatEvery,
      SelectedDays: typeof task.SelectedDays === 'object' && task.SelectedDays !== null 
        ? JSON.stringify(task.SelectedDays) 
        : task.SelectedDays,
      MonthlyDay: task.MonthlyDay,
      ProposalId: proposalId,
      ProposalVersionId: proposalVersionId || null,
      bypassGate: true,
    };

    const taskRes = await axios.post(
      `${process.env.REACT_APP_API_URL}/proposalRoute/${proposalId}/tasks/add`,
      fullTask, {
      headers: {
        Authorization: process.env.REACT_APP_BasicAuthSecret
      }
    }
    );

    return taskRes.data;
  };

  const saveSubtasks = async (taskId, proposalTaskId, fullTask, proposalId) => {
    const subtasksRes = await axios.get(
      `${process.env.REACT_APP_API_URL}/subtaskRoute/subtasks/${taskId}`, {
      headers: {
        Authorization: process.env.REACT_APP_BasicAuthSecret
      }
    }
    );
    const fetchedSubtasks = subtasksRes.data || [];

    if (fetchedSubtasks.length > 0) {
      await axios.post(`${process.env.REACT_APP_API_URL}/proposalRoute/subtasks/replace`, {
        proposalId,
        bypassGate: true,
        subtasks: fetchedSubtasks.map((st) => ({
          ProposalTaskId: proposalTaskId,
          SubtaskId: st.SubtaskId,
          SelectedDays: fullTask.SelectedDays,
          MonthlyDay: fullTask.MonthlyDay,
        })),
      },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
    }
  };

   const isTaskValid =
    Boolean(taskId) &&
    Boolean(selectedFrequency) &&
    (
      (selectedFrequency === "Daily" &&
        dailyStartTimes.length > 0 &&
        dailyStartTimes.every((t) => Boolean(t)) &&
        new Set(dailyStartTimes.map((t) => moment(t).format("HH:mm"))).size === dailyStartTimes.length) ||
      (selectedFrequency === "Weekly" && selectedDays.length > 0) ||
      (selectedFrequency === "Monthly" &&
        (monthlyType === "specific_day"
          ? Boolean(monthlyDay)
          : monthlyOccurrences.length > 0)) ||
      (selectedFrequency === "OneTime" && Boolean(oneTimeDate)) ||
      (selectedFrequency === "Yearly" && yearlyDates.length > 0)
    );

  const resetProposalForm = () => {
    reset();                 // reset react-hook-form
    setStep(1);
    setTasks([]);
    setSelectedFrequency("Weekly");
    setSelectedDays([]);
    setRepeatEvery(1);
    setMonthlyDay(new Date().getDate());
    setYearlyMonth([new Date().getMonth()]);
    setYearlyDay(new Date().getDate());
    setMonthlyType("specific_day");
    setMonthlyOccurrences([{ occurrence: "1st", weekday: "monday" }]);
    setOneTimeDate(null);
  };

  const handleFinalSave = async () => {
    if (tasks.length === 0) {
      toast.error(t("saveAtLeastOneTask"));
      return;
    }

    setIsLoading(true);
    try {
      const createdProposal = await saveProposal(proposalData);
      const createdProposalId = createdProposal.ProposalId;
      const createdVersionId = createdProposal.CurrentVersionId || null;

      for (const task of tasks) {
        const savedTask = await saveTask(task, createdProposalId, createdVersionId);
        await saveSubtasks(task.TaskId, savedTask.ProposalTaskId, task, createdProposalId);
      }

      if (onSaveFinal) onSaveFinal(createdProposal);

      toast.success(t("proposalTasksSaved"), { position: 'top-right', autoClose: 2000 });
      handleClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save proposal.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
    <Modal
      show={show}
      onHide={handleClose}
      onExited={resetProposalForm}
            centered
      dialogClassName="task-details-modal"
    >
      <Modal.Header closeButton className="d-flex justify-content-between align-items-center">
        <Modal.Title>
          {step === 1 ? t("newProposal") : t("taskDetails")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {step === 1 ? (
          <>
            <Row>
              <Col md="6" className="mb-0">
                <Form.Label className="data-font-size">
                  {t("workType")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Group className="mb-3">
                  <Controller
                    name="templateId"
                    control={control}
                    rules={{ required: t("workTypeRequired") }}
                    render={({ field, fieldState: { error } }) => {
                      const workTypeOptions = templates.map((template) => ({
                        value: template.TemplateId,      // for saving
                        label: template.TemplateName,    // for display
                        workType: template.WorkType,     // to store in form
                      }));

                      return (
                        <>
                          <input type="hidden" {...register("workType")} /> {/* Keep workType synced */}

                          <Select
                            key={i18n.language}
                            options={workTypeOptions}
                            isClearable
                            value={workTypeOptions.find((opt) => opt.value === field.value) || null}
                            onChange={(selected) => {
                              const selectedValue = selected ? selected.value : "";
                              const selectedWorkType = selected ? selected.workType : "";
                              field.onChange(selectedValue); // set templateId
                              setValue("templateId", selectedValue);
                              setValue("workType", selectedWorkType);
                              setSelectedWorkType(selectedWorkType);
                            }}
                            onBlur={field.onBlur}
                            className={`custom-select-container ${error ? "is-invalid" : ""}`}
                            classNamePrefix="custom-select"
                            placeholder={t("select")}
                            noOptionsMessage={() => t("noOptionsAvailable")}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldScrollIntoView={false}
                          />
                          {error && (
                            <div className="invalid-feedback d-block">{error.message}</div>
                          )}
                        </>
                      );
                    }}
                  />

                </Form.Group>
              </Col>

              <Col md="6">
                <Form.Label>{t("startDate")}<span className="text-danger">*</span></Form.Label>
                <Controller
                  control={control}
                  rules={{ required: t("startDateRequired") }}
                  name="startDate"
                  render={({ field }) => (
                    <DatePicker
                      selected={field.value}
                      onChange={field.onChange}
                      dateFormat="dd-MM-yyyy"
                      className={`form-control ${errors.startDate ? "is-invalid" : ""}`}
                      placeholderText="dd-mm-yyyy"
                      portalId="date-picker-portal"
                    />
                  )}
                />
                {errors.startDate && <div className="invalid-feedback d-block">{errors.startDate.message}</div>}
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md="6" className="mb-0">
                <Form.Label className="data-font-size">
                  {t("paymentConditions")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Group className="mb-3">
                  <Controller
                    name="paymentTerms"
                    control={control}
                    rules={{ required: t("paymentTermsRequired") }}
                    render={({ field, fieldState: { error } }) => {
                      const options = [
                        { value: "Payment Request", label: t("paymentRequest") },
                        { value: "Cash", label: t("cash") },
                        { value: "Bank Transfer", label: t("bankTransfer") },
                      ];

                      return (
                        <>
                          <Select
                            key={i18n.language}
                            options={options}
                            isClearable
                            value={options.find((opt) => opt.value === field.value) || null}
                            onChange={(s) => field.onChange(s ? s.value : "")}
                            onBlur={field.onBlur}
                            className={`custom-select-container ${error ? "is-invalid" : ""
                              }`}
                            classNamePrefix="custom-select"
                            placeholder={t("select")}
                            noOptionsMessage={() => t("noOptionsAvailable")}
                          />
                          {error && (
                            <div className="invalid-feedback d-block">
                              {error.message}
                            </div>
                          )}
                        </>
                      );
                    }}
                  />
                </Form.Group>
              </Col>

              <Col md="6" className="mb-0">
                <Form.Label className="data-font-size">
                  {t("invoiceType")} <span className="text-danger">*</span>
                </Form.Label>
                <Form.Group className="mb-3">
                  <Controller
                    name="invoiceType"
                    control={control}
                    rules={{ required: t("invoiceTypeRequired") }}
                    render={({ field, fieldState: { error } }) => {
                      const options = [
                        { value: "Invoice", label: t("invoice") },
                        { value: "Proforma Invoice", label: t("proformaInvoice") },
                      ];

                      return (
                        <>
                          <Select
                            key={i18n.language}
                            options={options}
                            isClearable
                            value={options.find((opt) => opt.value === field.value) || null}
                            onChange={(s) => field.onChange(s ? s.value : "")}
                            onBlur={field.onBlur}
                            className={`custom-select-container ${error ? "is-invalid" : ""
                              }`}
                            classNamePrefix="custom-select"
                            placeholder={t("select")}
                            noOptionsMessage={() => t("noOptionsAvailable")}
                          />
                          {error && (
                            <div className="invalid-feedback d-block">
                              {error.message}
                            </div>
                          )}
                        </>
                      );
                    }}
                  />
                </Form.Group>
              </Col>

            </Row>
          </>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label className="data-font-size fw-semi-bold">
                {t("task")} <span className="text-danger">*</span>
              </Form.Label>
              <Controller
                name="taskId"
                control={control}
                rules={{ required: t("taskRequired") }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Select
                      key={i18n.language}
                      options={taskOptions}
                      isClearable
                      value={
                        taskOptions.find((opt) => opt.value === field.value) || null
                      }
                      onChange={(s) => field.onChange(s ? s.value : null)}
                      onBlur={field.onBlur}
                      className={`custom-select-container ${error ? "is-invalid" : ""}`}
                      classNamePrefix="custom-select"
                      placeholder={t("select")}
                      noOptionsMessage={() => t("noOptionsAvailable")}
                    />
                    {error && (
                      <div className="invalid-feedback d-block">
                        {error.message}
                      </div>
                    )}
                  </>
                )}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="data-font-size fw-semi-bold">
                {t("frequency")} <span className="text-danger">*</span>
              </Form.Label>
              <Controller
                name="frequency"
                control={control}
                rules={{ required: t("frequencyRequired") }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Nav
                      variant="pills"
                      activeKey={selectedFrequency}
                      onSelect={(selectedKey) => {
                        field.onChange(selectedKey);
                        setSelectedFrequency(selectedKey);
                      }}
                      className="frequency-tabs-nav mb-1"
                    >
                      <Nav.Item>
                        <Nav.Link eventKey="OneTime">{t("onetime")}</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="Daily">{t("daily")}</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="Weekly">{t("weekly")}</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="Monthly">{t("monthly")}</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="Yearly">{t("yearly")}</Nav.Link>
                      </Nav.Item>
                    </Nav>
                    {error && !selectedFrequency && (
                      <div className="invalid-feedback d-block">
                        {error.message}
                      </div>
                    )}
                  </>
                )}
              />
            </Form.Group>

            {selectedFrequency === "OneTime" && (
              <div className="recurrence-settings-card">
                <div className="recurrence-settings-header">
                  <i className="far fa-clock" />
                  <span>{t("recurrenceSettings")}</span>
                </div>
                <Form.Group className="mb-0">
                  <Form.Label className="data-font-size fw-semi-bold">
                    {t("taskDate") || "Task Date"} <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="task-date-input-wrap">
                    <DatePicker
                      selected={oneTimeDate}
                      onChange={(selectedDate) => setOneTimeDate(selectedDate)}
                      dateFormat="dd-MM-yyyy"
                      className="form-control"
                      placeholderText="dd-mm-yyyy"
                    />
                  </div>
                </Form.Group>
              </div>
            )}

            {selectedFrequency === "Daily" && (
              <div className="recurrence-settings-card">
                <div className="recurrence-settings-header">
                  <i className="far fa-clock" />
                  <span>{t("recurrenceSettings")}</span>
                </div>
                <Form.Group className="mb-0">
                  <Form.Label className="data-font-size fw-semi-bold">
                    {t("startsAt")} <span className="text-danger">*</span>
                  </Form.Label>
                  {dailyStartTimes.map((time, idx) => (
                    <div key={idx} className="recurrence-occurrence-row">
                      <DatePicker
                        selected={time}
                        onChange={(val) => {
                          const newTimeStr = moment(val).format("HH:mm");
                          const isDuplicate = dailyStartTimes.some(
                            (t, i) => i !== idx && moment(t).format("HH:mm") === newTimeStr
                          );
                          if (isDuplicate) {
                            toast.error(t("dailyTimeDuplicate") || "That time is already added.", {
                              position: "top-right",
                              autoClose: 2500,
                            });
                            return;
                          }
                          setDailyStartTimes((prev) => {
                            const arr = [...prev];
                            arr[idx] = val;
                            return arr;
                          });
                        }}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={15}
                        timeCaption="Time"
                        dateFormat="HH:mm"
                        timeFormat="HH:mm"
                        placeholderText="HH:mm"
                        className="form-control"
                        customInput={
                           <TimeInput className="form-control form-control-sm w-auto" />
                        }
                      />
                      {dailyStartTimes.length > 1 && (
                        <button
                          type="button"
                          className="remove-day-btn"
                          onClick={() => removeDailyStartTimeRow(idx)}
                          aria-label={t("remove") || "Remove"}
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  ))}

                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 mt-1 text-decoration-none add-day-link"
                    onClick={addDailyStartTimeRow}
                  >
                    {t("addAnotherTime") || "+ Add another time"}
                  </Button>
                </Form.Group>
              </div>
            )}
            {selectedFrequency === "Weekly" && (
              <div className="recurrence-settings-card">
                <div className="recurrence-settings-header">
                  <i className="far fa-clock" />
                   <span>{t("recurrenceSettings")}</span>
                </div>
                <Form.Group className="mb-0">
                  <Form.Label className="data-font-size fw-semi-bold">
                    {t("weekday")} <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {dayKeys.map((dayKey) => (
                      <div key={dayKey}>
                        <input
                          type="checkbox"
                          {...register(`weekdays.${dayKey}`)}
                          checked={selectedDays.includes(dayKey)}
                          readOnly
                          hidden
                        />
                        <Button
                          variant={selectedDays.includes(dayKey) ? "success" : "outline-secondary"}
                          size="sm"
                          className="weekday-btn"
                          onClick={() => handleDayToggle(dayKey)}
                        >
                          {t(`days.${dayKey}`)}
                        </Button>
                      </div>
                    ))}
                  </div>                  
                </Form.Group>
              </div>
            )}

            {selectedFrequency === "Monthly" && (
              <div className="recurrence-settings-card">
                <div className="recurrence-settings-header">
                  <i className="far fa-clock" />
                  <span>{t("recurrenceSettings")}</span>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label className="data-font-size fw-semi-bold">
                    {t("repeatBy") || "Repeat By"} <span className="text-danger">*</span>
                  </Form.Label>
                  <Row className="g-2">
                    <Col xs={6}>
                      <div
                        className={`recurrence-radio-card ${monthlyType === "weekday_occurrence" ? "active" : ""}`}
                        onClick={() => setMonthlyType("weekday_occurrence")}
                      >
                        <Form.Check
                          type="radio"
                          name="monthlyTypeRadio"
                          checked={monthlyType === "weekday_occurrence"}
                          onChange={() => setMonthlyType("weekday_occurrence")}
                          className="mb-0"
                        />
                        <span>{t("dayOfWeek") || "Day of Week"}</span>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div
                        className={`recurrence-radio-card ${monthlyType === "specific_day" ? "active" : ""}`}
                        onClick={() => setMonthlyType("specific_day")}
                      >
                        <Form.Check
                          type="radio"
                          name="monthlyTypeRadio"
                          checked={monthlyType === "specific_day"}
                          onChange={() => setMonthlyType("specific_day")}
                          className="mb-0"
                        />
                        <span>{t("dayOfMonth") || "Day of Month"}</span>
                      </div>
                    </Col>
                  </Row>
                </Form.Group>

                  {monthlyType === "specific_day" ? (
                  <Row className="g-3 mb-2">
                    <Col xs={12} sm={5}>
                      <Form.Group>
                        <Form.Label className="data-font-size fw-semi-bold">
                          {t("onDay") || "On Day"} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          value={monthlyDay}
                          onChange={(e) => {
                            const val = e.target.value;
                            setMonthlyDay(val === "last" ? "last" : Number(val));
                          }}
                          className="recurrence-inline-select"
                        >
                          {[...Array(31).keys()].slice(1).map((num) => (
                            <option key={num} value={num}>
                              {num}
                            </option>
                          ))}
                          <option value="last">{t("lastDay") || "Last Day"}</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>
                ) : (
                  <Form.Group className="mb-2">
                    <Form.Label className="data-font-size fw-semi-bold">
                      {t("weekday")} <span className="text-danger">*</span>
                    </Form.Label>
                    {monthlyOccurrences.map((occ, idx) => (
                      <div key={idx} className="recurrence-occurrence-row">
                        <Form.Select
                          value={occ.occurrence}
                          onChange={(e) => {
                            const val = e.target.value;
                            setMonthlyOccurrences((prev) => {
                              const arr = [...prev];
                              arr[idx] = { ...arr[idx], occurrence: val };
                              return arr;
                            });
                          }}
                          className="w-auto"
                        >
                          <option value="1st">{t("1st") || "1st"}</option>
                          <option value="2nd">{t("2nd") || "2nd"}</option>
                          <option value="3rd">{t("3rd") || "3rd"}</option>
                          <option value="4th">{t("4th") || "4th"}</option>
                          <option value="last">{t("last") || "Last"}</option>
                        </Form.Select>

                        <div className="d-flex flex-nowrap gap-1">
                          {dayKeys.map((dayKey) => (
                            <Button
                              key={dayKey}
                              variant={occ.weekday === dayKey ? "success" : "outline-secondary"}
                              size="sm"
                              className="weekday-btn"
                              onClick={() => {
                                setMonthlyOccurrences((prev) => {
                                  const arr = [...prev];
                                  arr[idx] = { ...arr[idx], weekday: dayKey };
                                  return arr;
                                });
                              }}
                            >
                              {t(`days.${dayKey}`)}
                            </Button>
                          ))}
                        </div>
                        {monthlyOccurrences.length > 1 && (
                          <button
                            type="button"
                            className="remove-day-btn"
                            onClick={() => removeMonthlyOccurrenceRow(idx)}
                            aria-label={t("remove") || "Remove"}
                          >
                            &times;
                          </button>
                        )}
                      </div>
                    ))}

                    {monthlyOccurrences.length < MAX_MONTHLY_OCCURRENCE_ROWS && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 mt-1 text-decoration-none add-day-link"
                        onClick={addMonthlyOccurrenceRow}
                      >
                        {t("addAnotherDay") || "+ Add another day"}
                      </Button>
                    )}
                  </Form.Group>
                )}
              </div>
            )}

            {selectedFrequency === "Yearly" && (
              <div className="recurrence-settings-card">
                <div className="recurrence-settings-header">
                  <i className="far fa-clock" />
                   <span>{t("recurrenceSettings")}</span>
                </div>
                <Form.Group className="mb-2">
                  <Form.Label className="data-font-size fw-semi-bold">
                    {t("onDay") || "On Day"} <span className="text-danger">*</span>
                  </Form.Label>
                  {yearlyDates.map((entry, idx) => (
                    <div key={idx} className="recurrence-occurrence-row">
                      <Form.Select
                        value={entry.day}
                        onChange={(e) => {
                          const val = e.target.value;
                          setYearlyDates((prev) => {
                            const arr = [...prev];
                            arr[idx] = { ...arr[idx], day: val === "last" ? "last" : Number(val) };
                            return arr;
                          });
                        }}
                        className="recurrence-inline-select"
                      >
                        {[...Array(31).keys()].slice(1).map((num) => (
                          <option key={num} value={num}>
                            {num}
                          </option>
                        ))}
                        <option value="last">{t("lastDay") || "Last Day"}</option>
                      </Form.Select>
                      <span className="text-muted font-size-13">{t("of") || "of"}</span>
                      <Form.Select
                        value={entry.month}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          setYearlyDates((prev) => {
                            const arr = [...prev];
                            arr[idx] = { ...arr[idx], month: val };
                            return arr;
                          });
                        }}
                        className="recurrence-inline-select"
                      >
                        {moment.months().map((month, mIdx) => (
                          <option key={month} value={mIdx}>
                            {t(`shortMonths.${month.toLowerCase()}`)}
                          </option>
                        ))}
                      </Form.Select>
                      {yearlyDates.length > 1 && (
                        <button
                          type="button"
                          className="remove-day-btn"
                          onClick={() => removeYearlyDateRow(idx)}
                          aria-label={t("remove") || "Remove"}
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  ))}

                  {yearlyDates.length < MAX_YEARLY_OCCURRENCE_ROWS && (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 mt-1 text-decoration-none add-day-link"
                      onClick={addYearlyDateRow}
                    >
                      {t("addAnotherDay") || "+ Add another day"}
                    </Button>
                  )}
                </Form.Group>
              </div>
            )}

            {getRecurrenceSummary() && (
              <div className="recurrence-summary-box mb-3">
                <div className="recurrence-summary-icon">
                  <i className="fas fa-check-circle" />
                </div>
                <div className="recurrence-summary-content">
                  <div className="recurrence-summary-title">
                    {t("recurrenceSummary")}
                  </div>
                  <div className="recurrence-summary-text">
                    {getRecurrenceSummary()}
                  </div>
                </div>
              </div>
            )}
            {tasks.length > 0 && (
              <div className="mt-4">
                <hr />
                <h5>{t("savedTasks")} ({tasks.length})</h5>
                <div className="saved-task-list">
                  {tasks.map((task, idx) => (
                    <div key={idx} className="saved-task-row">
                      <div className="saved-task-row-top">
                        <span className="saved-task-name">{task.TaskName}</span>
                        <span className="badge bg-primary-subtle text-primary saved-task-badge">
                          {t(task.Frequency.toLowerCase())}
                        </span>
                        <button
                          type="button"
                          className="saved-task-delete-btn"
                          onClick={() => setTaskPendingDeleteIdx(idx)}
                          aria-label={`${t("delete")} ${task.TaskName}`}
                        >
                          <i className="fa fa-trash" aria-hidden="true"></i>
                        </button>
                      </div>
                      <div className="saved-task-schedule">
                        {describeRecurrence(task, t)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {step === 2 ? (
          <>
            <Button variant="falcon-default" onClick={() => setStep(1)}>{t("back")}</Button>
            <Button
              variant="success"
              onClick={handleAddTask}
              disabled={!isTaskValid}
            >
              + {tasks.length > 0 ? t("addAnotherTask") : t("addTask")}
            </Button>
            {tasks.length > 0 && (
              <Button
                variant="primary"
                onClick={handleFinalSave}
                disabled={isLoading}
              >
                {isLoading ? t("saving") : t("save")}
              </Button>
            )}
          </>
        ) : (
          <Button
            variant="primary"
            onClick={handleSubmit(handleProposalNext)}
            disabled={isLoading}
          >
            {t("next")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>

    <Modal
      show={taskPendingDeleteIdx !== null}
      onHide={() => setTaskPendingDeleteIdx(null)}
      backdrop="static"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{t("confirmation")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {t("confirmDeleteTaskWithSubtasks", {
          taskName: taskPendingDeleteIdx !== null ? tasks[taskPendingDeleteIdx]?.TaskName : "",
        })}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="falcon-default" onClick={() => setTaskPendingDeleteIdx(null)}>
          {t("cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            setTasks((prev) => prev.filter((_, i) => i !== taskPendingDeleteIdx));
            setTaskPendingDeleteIdx(null);
          }}
        >
          {t("delete")}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};